import { css, styled, keyframes, useTheme } from "styled-components";
import React, { useState, useEffect, useRef, useMemo, useCallback, Fragment, Component, createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { range, Ordering, clamp, Sentry, wait } from "@idk-web/core-utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TailSpin } from "react-loader-spinner";
import { lastEventId, showReportDialog } from "@sentry/react";
import DatePicker from "react-date-picker";
import DateTimePicker from "react-datetime-picker";
import ReactSelect from "react-select";
import { createPortal } from "react-dom";
import { NavLink, useLocation, matchPath, Link as Link$2, useHistory } from "react-router-dom";
const Styling = {
  prop(name) {
    return (props) => props[name];
  },
  typography(type) {
    return (context) => css`
      font-family: ${context.theme.typography[type].fontFamily};
      font-size: ${context.theme.typography[type].fontSize};
      font-weight: ${context.theme.typography[type].fontWeight};
      letter-spacing: ${context.theme.typography[type].letterSpacing};
      line-height: ${context.theme.typography[type].lineHeight};
    `;
  },
  shadow(name) {
    return (context) => context.theme.shadows[name];
  },
  breakpoint(name) {
    return (context) => context.theme.breakpoints[name];
  },
  spacing(...names) {
    return (context) => names.map((name) => context.theme.spacing[name]).join(" ");
  },
  radii(...names) {
    return (context) => names.map((name) => context.theme.radii[name]).join(" ");
  },
  zIndex(name) {
    return (context) => context.theme.zIndex[name];
  }
};
const StandardCard = styled.div`
  background-color: ${({ theme }) => theme.palette.grayscale.white};
  border-radius: ${Styling.radii("card")};
  box-shadow: ${Styling.shadow("card")};
  padding: ${Styling.spacing(6)};
  margin: ${Styling.spacing(6)};
  width: 100%;
  @media (max-width: ${Styling.breakpoint("lg")}px) {
    padding: ${Styling.spacing(4)};
    margin: ${Styling.spacing(4)};
  }
  @media (max-width: ${Styling.breakpoint("sm")}px) {
    padding: ${Styling.spacing(2)};
    margin: ${Styling.spacing(2)};
  }
`;
const Container$f = styled(StandardCard)`
  position: relative;
  border-radius: 4px;
  padding: 0;
  margin: 0;
  width: 100%;
  overflow: hidden;
  background: linear-gradient(
    10deg,
    #6acf47 25%,
    #268903 100%
  ); // TODO move to theme
`;
const InnerContainer = styled.div`
  position: relative;
  padding: ${Styling.spacing(3)};
  @media (max-width: ${Styling.breakpoint("sm")}px) {
    padding: ${Styling.spacing(2)};
  }
`;
const Overlay$2 = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  background: radial-gradient(
    circle,
    ${({ theme }) => theme.palette.primary.main} 0%,
    #fdb72a 100%
  ); // TODO move to theme
  opacity: 0;
  transition: opacity 200ms;

  &.visible {
    opacity: 1;
  }
`;
const CircleArea = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;
const float = (height) => keyframes`
  from {
    transform: translate(-50%, calc(-50% - ${height}));
  }
  to {
    transform: translate(-50%, calc(-50% + ${height}));
  }
`;
const Circle = styled.div`
  position: absolute;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  top: ${({ y }) => y * 100}%;
  left: ${({ x }) => x * 100}%;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.grayscale.white};
  opacity: 0.1;
  animation-name: ${({ moveHeight }) => float(`${moveHeight}%`)};
  animation-duration: ${({ moveHeight, size }) => moveHeight / 10 + size / 100 + 2}s;
  animation-direction: alternate-reverse;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
`;
const AuthCard = ({
  className,
  success,
  circleCount = 5,
  circleMaxSize = 300,
  circleMinSize = 100,
  children
}) => {
  const [containerElement, setContainerElement] = useState(null);
  const [circles, setCircles] = useState([]);
  useEffect(() => {
    if (!containerElement) {
      return;
    }
    const arr = [];
    for (let i = 0; i < circleCount; i++) {
      arr.push({
        size: Math.random() * circleMaxSize + circleMinSize,
        x: Math.random(),
        y: Math.random(),
        moveHeight: 10 + Math.random() * 20
      });
    }
    setCircles(arr);
  }, [containerElement]);
  return /* @__PURE__ */ React.createElement(Container$f, { className, ref: (el) => setContainerElement(el) }, /* @__PURE__ */ React.createElement(Overlay$2, { className: success ? "" : "visible" }), /* @__PURE__ */ React.createElement(CircleArea, null, circles.map((props, i) => /* @__PURE__ */ React.createElement(Circle, { key: i, ...props }))), /* @__PURE__ */ React.createElement(InnerContainer, null, children));
};
var faMinus = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "minus";
  var width = 448;
  var height = 512;
  var aliases = [8211, 8722, 10134, "subtract"];
  var unicode = "f068";
  var svgPathData = "M432 256c0 17.7-14.3 32-32 32L48 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l352 0c17.7 0 32 14.3 32 32z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faMinus = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faMinus);
var faArrowUp = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "arrow-up";
  var width = 384;
  var height = 512;
  var aliases = [8593];
  var unicode = "f062";
  var svgPathData = "M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 141.2V448c0 17.7 14.3 32 32 32s32-14.3 32-32V141.2L329.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faArrowUp = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faArrowUp);
var faArrowDown = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "arrow-down";
  var width = 384;
  var height = 512;
  var aliases = [8595];
  var unicode = "f063";
  var svgPathData = "M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8 224 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faArrowDown = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faArrowDown);
const fadeIn$2 = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const Container$e = styled.div`
  position: absolute;
  z-index: ${Styling.zIndex("backdrop")};
  background-color: ${({ color }) => color};
  width: 100%;
  height: 100%;
  opacity: ${({ show }) => show ? 1 : 0};
  animation: ${fadeIn$2} ${({ durationMs }) => durationMs}ms linear 1;
  transition: opacity ${({ durationMs }) => durationMs}ms;
  pointer-events: ${({ show }) => show ? "all" : "none"};
`;
const Overlay$1 = ({
  className,
  show,
  color = "rgba(255, 255, 255, 0.5)",
  durationMs = 100,
  onClick,
  onFullyVisible,
  onFullyGone,
  children
}) => {
  useEffect(() => {
    let handle;
    if (show) {
      handle = setTimeout(() => onFullyVisible == null ? void 0 : onFullyVisible(), durationMs);
    } else {
      handle = setTimeout(() => onFullyGone == null ? void 0 : onFullyGone(), durationMs);
    }
    return () => clearTimeout(handle);
  }, [show]);
  return /* @__PURE__ */ React.createElement(
    Container$e,
    {
      className,
      show,
      color,
      durationMs,
      onClick
    },
    children
  );
};
function flexGap(gap) {
  if (isFlexGapSupported()) {
    return css`
      gap: ${gap};
    `;
  }
  return polyfill(gap);
}
let flexGapSupported = null;
function isFlexGapSupported() {
  var _a;
  if (flexGapSupported !== null) {
    return flexGapSupported;
  }
  const flex = document.createElement("div");
  flex.style.display = "flex";
  flex.style.flexDirection = "column";
  flex.style.rowGap = "1px";
  flex.appendChild(document.createElement("div"));
  flex.appendChild(document.createElement("div"));
  document.body.appendChild(flex);
  flexGapSupported = flex.scrollHeight === 1;
  (_a = flex == null ? void 0 : flex.parentNode) == null ? void 0 : _a.removeChild(flex);
  return flexGapSupported;
}
function polyfill(gap) {
  return css`
    margin-left: -${gap};
    margin-top: -${gap};

    & > * {
      margin-left: ${gap};
      margin-top: ${gap};
    }
  `;
}
const Box = styled.div(
  ({ direction = "horizontal", alignX, alignY, canWrap, spacing }) => {
    const styling = {
      width: "100%",
      display: "flex",
      flexDirection: direction === "horizontal" ? "row" : "column"
    };
    const justifyContent = (align) => {
      switch (align) {
        case "center":
        case "space-around":
        case "space-between":
          return align;
        case "top":
        case "left":
          return "start";
        case "right":
        case "bottom":
          return "end";
        default:
          return "stretch";
      }
    };
    const alignContent = (align) => {
      switch (align) {
        case "center":
        case "space-around":
        case "space-between":
          return align;
        case "top":
        case "left":
          return "start";
        case "right":
        case "bottom":
          return "end";
        default:
          return "stretch";
      }
    };
    const alignItems = (align) => {
      switch (align) {
        case "center":
          return align;
        case "top":
        case "left":
          return "start";
        case "right":
        case "bottom":
          return "end";
        default:
          return "stretch";
      }
    };
    if (canWrap === true) {
      styling.flexWrap = "wrap";
    } else if (canWrap === false) {
      styling.flexWrap = "nowrap";
    }
    if (direction === "vertical") {
      styling.justifyContent = justifyContent(alignY);
      if (canWrap) {
        styling.alignContent = alignContent(alignX);
      } else {
        styling.alignItems = alignItems(alignX);
      }
    } else {
      styling.justifyContent = justifyContent(alignX);
      if (canWrap) {
        styling.alignContent = alignContent(alignY);
      } else {
        styling.alignItems = alignItems(alignY);
      }
    }
    if (spacing) {
      if (isFlexGapSupported()) {
        styling.gap = spacing;
      } else {
        if (direction === "horizontal") {
          styling["& > * + *"] = {
            marginLeft: spacing
          };
        } else {
          styling["& > * + *"] = {
            marginTop: spacing
          };
        }
      }
    }
    return styling;
  }
);
const SpinningCircleLoader = () => {
  const theme = useTheme();
  return /* @__PURE__ */ React.createElement(Box, { alignX: "center" }, /* @__PURE__ */ React.createElement(
    TailSpin,
    {
      width: `64px`,
      height: `64px`,
      color: theme.palette.primary.main,
      ariaLabel: "loading-indicator"
    }
  ));
};
const blink = keyframes`
  0% {
    opacity: 0.1;
  }
  30% {
    opacity: 0.9;
  }
  100% {
    opacity: 0.1;
  }
`;
const Bar$1 = styled.span`
  width: 10px;
  height: 18px;
  opacity: 0.1;
  background: white;
  border-radius: 4px;
  animation: ${blink} ${Styling.prop("time")}s infinite;
`;
const BlinkingBarsLoader = ({
  time = 1
}) => {
  return /* @__PURE__ */ React.createElement(Box, { alignX: "center", spacing: "6px" }, range(0, 8).map((index) => /* @__PURE__ */ React.createElement(
    Bar$1,
    {
      key: index,
      time,
      style: { animationDelay: `${time / 9 * index}s` }
    }
  )));
};
var faCheck = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "check";
  var width = 448;
  var height = 512;
  var aliases = [10003, 10004];
  var unicode = "f00c";
  var svgPathData = "M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faCheck = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faCheck);
const Label$2 = styled.label`
  ${Styling.typography("body")}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => flexGap(theme.spacing[1])};
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "pointer"};
  user-select: none;
`;
const BareSquare = styled.div(
  ({ hover, checked, disabled, focus, theme }) => ({
    position: "relative",
    visibility: "visible",
    display: "flex",
    flexShrink: 0,
    alignSelf: "start",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    color: theme.palette.primary.main,
    border: `2px solid ${focus ? theme.palette.primary.main : theme.palette.grayscale.light2}`,
    // TODO move to theme
    borderRadius: theme.radii.input,
    opacity: disabled ? 0.5 : 1,
    backgroundColor: disabled ? theme.palette.grayscale.light4 : theme.palette.grayscale.white,
    ...hover && !disabled ? {
      backgroundColor: theme.palette.grayscale.light5
    } : {},
    ...checked && !disabled ? {
      border: focus ? `2px solid ${theme.palette.primary.light2}` : 0
    } : {}
  })
);
const FilledSquare = styled.div(
  ({ hover, checked, disabled, focus, theme }) => ({
    position: "relative",
    visibility: "visible",
    display: "flex",
    flexShrink: 0,
    alignSelf: "start",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    color: theme.palette.grayscale.white,
    border: `2px solid ${focus ? theme.palette.primary.main : theme.palette.grayscale.light2}`,
    // TODO move to theme
    borderRadius: theme.radii.input,
    opacity: disabled ? 0.5 : 1,
    backgroundColor: disabled ? theme.palette.grayscale.light4 : theme.palette.grayscale.white,
    ...hover && !disabled ? {
      backgroundColor: theme.palette.grayscale.light5
    } : {},
    ...checked && !disabled ? {
      backgroundColor: `${theme.palette.primary.main} !important`,
      border: focus ? `2px solid ${theme.palette.primary.light2}` : 0
    } : {},
    transition: "100ms background-color, 100ms border"
  })
);
const Input$1 = styled.input({
  position: "absolute",
  opacity: 0,
  height: 0,
  width: 0,
  cursor: "pointer"
});
const Checkmark$1 = styled.span(({ disabled, theme }) => ({
  position: "absolute",
  color: `${disabled ? theme.palette.grayscale.dark3 : "inherit"}`
}));
const BareCheckbox = ({
  className,
  checked,
  disabled,
  label,
  ref: _ref,
  icon,
  ...props
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  useEffect(
    () => setIsChecked(checked ?? Boolean(props.value)),
    [checked, props.value]
  );
  return /* @__PURE__ */ React.createElement(
    Label$2,
    {
      className,
      disabled,
      onMouseEnter: () => setIsHovering(true),
      onMouseLeave: () => setIsHovering(false)
    },
    /* @__PURE__ */ React.createElement(
      BareSquare,
      {
        hover: isHovering,
        checked: isChecked,
        disabled,
        focus: hasFocus
      },
      isChecked && /* @__PURE__ */ React.createElement(
        Checkmark$1,
        {
          hover: isHovering,
          checked: isChecked,
          disabled,
          focus: hasFocus
        },
        icon ?? /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faCheck.faCheck, size: "lg" })
      )
    ),
    label,
    /* @__PURE__ */ React.createElement(
      Input$1,
      {
        type: "checkbox",
        ...props,
        checked: isChecked,
        disabled,
        onFocus: (evt) => {
          var _a;
          setHasFocus(true);
          (_a = props.onFocus) == null ? void 0 : _a.call(props, evt);
        },
        onBlur: (evt) => {
          var _a;
          setHasFocus(false);
          (_a = props.onBlur) == null ? void 0 : _a.call(props, evt);
        }
      }
    )
  );
};
const FilledCheckbox = ({
  className,
  checked,
  disabled,
  label,
  ref: _ref,
  icon,
  ...props
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  useEffect(
    () => setIsChecked(checked ?? Boolean(props.value)),
    [checked, props.value]
  );
  return /* @__PURE__ */ React.createElement(
    Label$2,
    {
      className,
      disabled,
      onMouseEnter: () => setIsHovering(true),
      onMouseLeave: () => setIsHovering(false)
    },
    /* @__PURE__ */ React.createElement(
      FilledSquare,
      {
        hover: isHovering,
        checked: isChecked,
        disabled,
        focus: hasFocus
      },
      isChecked && /* @__PURE__ */ React.createElement(
        Checkmark$1,
        {
          hover: isHovering,
          checked: isChecked,
          disabled,
          focus: hasFocus
        },
        icon ?? /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faCheck.faCheck, size: "lg" })
      )
    ),
    label,
    /* @__PURE__ */ React.createElement(
      Input$1,
      {
        type: "checkbox",
        ...props,
        checked: isChecked,
        disabled,
        onFocus: (evt) => {
          var _a;
          setHasFocus(true);
          (_a = props.onFocus) == null ? void 0 : _a.call(props, evt);
        },
        onBlur: (evt) => {
          var _a;
          setHasFocus(false);
          (_a = props.onBlur) == null ? void 0 : _a.call(props, evt);
        }
      }
    )
  );
};
const Container$d = styled(StandardCard)`
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  border-radius: 8px;
  border-collapse: collapse;
  overflow: auto;
  scrollbar-color: ${({ theme }) => theme.palette.primary.main} white;

  &::-webkit-scrollbar {
    width: 5px;
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.primary.main};
  }
`;
const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
`;
const Header$3 = styled.thead`
  ${Styling.typography("body")};
  color: ${({ theme }) => theme.palette.grayscale.white};
`;
const Body = styled.tbody`
  ${Styling.typography("body")};
`;
const Row = styled.tr`
  color: ${({ theme }) => theme.palette.primary.text};

  &:hover {
    background-color: #fbfbfb; // TODO move to theme
  }
`;
const HeaderCell = styled.th`
  background-color: ${({ theme }) => theme.palette.primary.main};
  padding: ${Styling.spacing(3, 1.5)};
  position: sticky;
  top: 0;
  text-align: ${({ align }) => align ?? "left"};
  white-space: nowrap;
`;
const DataCell = styled.td`
  padding: ${Styling.spacing(3, 1.5)};
  border-bottom: 1px solid #edf2f7; // TODO move to theme
`;
const ColumnSelectCheckbox = styled(BareCheckbox)`
  vertical-align: middle;

  & * {
    border: none !important;
  }
`;
const RowSelectCheckbox = styled(FilledCheckbox)`
  vertical-align: middle;
`;
const SortableField = styled(Box).attrs(({ theme }) => ({
  alignY: "center",
  spacing: theme.spacing[1]
}))`
  width: fit-content;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.light4};
  }
`;
const EmptyContent = styled(Box).attrs({
  alignX: "center",
  alignY: "center"
})`
  ${Styling.typography("body")}
  padding: ${Styling.spacing(6, 3)};
  color: ${({ theme }) => theme.palette.primary.text};
`;
const LoadingOverlay = ({ show, offset }) => {
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      style: {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: `${offset}px`,
        right: `-${offset}px`,
        pointerEvents: "none"
      }
    },
    /* @__PURE__ */ React.createElement(Overlay$1, { show, color: "rgba(255, 255, 255, 0.5)" }, /* @__PURE__ */ React.createElement(
      Box,
      {
        alignX: "center",
        alignY: "center",
        style: { position: "absolute", width: "100%", height: "100%" }
      },
      /* @__PURE__ */ React.createElement(SpinningCircleLoader, null)
    ))
  );
};
const DataTable = ({
  loading = false,
  multiSelect = false,
  selections,
  columns,
  rows,
  emptyText,
  onSort,
  onSelect
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  useEffect(() => {
    var _a;
    const handleScroll = () => {
      var _a2;
      if (ref.current) {
        setScrollLeft(((_a2 = ref.current) == null ? void 0 : _a2.scrollLeft) ?? 0);
      }
    };
    (_a = ref.current) == null ? void 0 : _a.addEventListener("scroll", handleScroll);
    return () => {
      var _a2;
      return (_a2 = ref.current) == null ? void 0 : _a2.removeEventListener("scroll", handleScroll);
    };
  }, []);
  if (emptyText === void 0) {
    emptyText = t("input.no_data");
  }
  return /* @__PURE__ */ React.createElement(Container$d, { ref }, /* @__PURE__ */ React.createElement(LoadingOverlay, { show: loading, offset: scrollLeft }), /* @__PURE__ */ React.createElement(Table, null, /* @__PURE__ */ React.createElement(Header$3, null, /* @__PURE__ */ React.createElement("tr", null, columns.map((column) => /* @__PURE__ */ React.createElement(
    HeaderCell,
    {
      key: column.key,
      align: column.headerAlignment,
      style: { width: column.headerWidth }
    },
    !column.sortable && column.header,
    column.sortable && /* @__PURE__ */ React.createElement(SortableField, { onClick: () => onSort == null ? void 0 : onSort(column.key) }, column.header, column.direction === Ordering.ASCENDING && /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faArrowDown.faArrowDown, size: "sm" }), column.direction === Ordering.DESCENDING && /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faArrowUp.faArrowUp, size: "sm" }))
  )), selections && /* @__PURE__ */ React.createElement(HeaderCell, null, multiSelect && rows.length > 0 && /* @__PURE__ */ React.createElement(
    ColumnSelectCheckbox,
    {
      icon: selections.length === rows.length ? void 0 : /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faMinus.faMinus, size: "lg" }),
      checked: selections.length > 0,
      onChange: () => onSelect == null ? void 0 : onSelect(
        selections.length === rows.length ? selections : [...rows, ...selections]
      )
    }
  )))), /* @__PURE__ */ React.createElement(Body, null, rows.map((row, index) => /* @__PURE__ */ React.createElement(Row, { key: String(index) }, columns.map((column) => /* @__PURE__ */ React.createElement(DataCell, { key: column.key }, column.accessor(row))), selections && /* @__PURE__ */ React.createElement(DataCell, null, /* @__PURE__ */ React.createElement(
    RowSelectCheckbox,
    {
      checked: selections.includes(row),
      onChange: () => onSelect == null ? void 0 : onSelect([row])
    }
  )))))), rows.length === 0 && /* @__PURE__ */ React.createElement(EmptyContent, null, !loading && emptyText));
};
const fadeIn$1 = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const LoaderWrapper$1 = styled.div`
  position: relative;
  height: 0;
  top: -12px;
  animation: ${fadeIn$1} 200ms linear 1;
`;
const Loader$1 = () => {
  const theme = useTheme();
  return /* @__PURE__ */ React.createElement(LoaderWrapper$1, null, /* @__PURE__ */ React.createElement(
    TailSpin,
    {
      width: `24px`,
      height: `24px`,
      color: theme.palette.secondary.light2,
      ariaLabel: "loading-indicator"
    }
  ));
};
const ButtonBase = styled.button`
  ${Styling.typography("button")}
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => flexGap(theme.spacing[2])};
  text-transform: uppercase;
  border: 0;
  border-radius: ${Styling.radii("button")};
  cursor: pointer;
  transition:
    color 80ms,
    background-color 80ms,
    border 80ms;
  min-width: 100px;

  &.lg {
    height: 56px;
    padding: 0 ${Styling.spacing(6)};
  }

  &.md {
    height: 44px;
    padding: 0 ${Styling.spacing(4)};
  }

  &.sm {
    height: 32px;
    padding: 0 ${Styling.spacing(2)};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
  }
`;
const Button$4 = ({
  className = "",
  size = "md",
  disabled,
  loading,
  loadingText,
  loader,
  startAdornment,
  endAdornment,
  text,
  ...props
}) => {
  const getText = () => {
    if (loading) {
      return loadingText ?? text;
    }
    return text;
  };
  className += " " + size;
  if (loading) {
    className += " loading";
  }
  return /* @__PURE__ */ React.createElement(ButtonBase, { className, disabled: disabled || loading, ...props }, loading && (loader ?? /* @__PURE__ */ React.createElement(Loader$1, null)), startAdornment, getText(), endAdornment);
};
const disabledButtonStyle$1 = css`
  &:disabled,
  &.loading {
    cursor: not-allowed;
    background-color: ${({ theme }) => theme.palette.disabled.main};
    color: ${({ theme }) => theme.palette.disabled.text};
    border: 1px solid ${({ theme }) => theme.palette.disabled.dark1};

    &:hover {
      background-color: ${({ theme }) => theme.palette.disabled.main};
    }
  }
`;
const PrimaryButton = styled(Button$4)`
  color: ${({ theme }) => theme.palette.grayscale.white};
  background-color: ${({ theme }) => theme.palette.primary.main};
  border: 1px solid ${({ theme }) => theme.palette.primary.main};

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.light1};
    border: 1px solid ${({ theme }) => theme.palette.primary.light1};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.primary.main};
    border: 1px solid ${({ theme }) => theme.palette.primary.main};
  }

  ${disabledButtonStyle$1}
`;
const SecondaryButton = styled(Button$4)`
  color: ${({ theme }) => theme.palette.primary.main};
  background-color: ${({ theme }) => theme.palette.grayscale.white};
  border: 1px solid white;

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.light5};
    border: 1px solid ${({ theme }) => theme.palette.primary.light5};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.primary.light4};
    border: 1px solid ${({ theme }) => theme.palette.primary.light4};
  }

  &.loading {
    background-color: white !important;
    border: 1px solid white !important;
  }

  ${disabledButtonStyle$1}
`;
const GrayButton = styled(Button$4)`
  color: ${({ theme }) => theme.palette.grayscale.white};
  background-color: ${({ theme }) => theme.palette.grayscale.dark1};
  border: 1px solid ${({ theme }) => theme.palette.grayscale.dark1};

  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale.main};
    border: 1px solid ${({ theme }) => theme.palette.grayscale.main};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.grayscale.dark1};
    border: 1px solid ${({ theme }) => theme.palette.grayscale.dark1};
  }

  ${disabledButtonStyle$1}
`;
const TertiaryButton = styled(Button$4)`
  color: ${({ theme }) => theme.palette.primary.main};
  background-color: ${({ theme }) => theme.palette.primary.light5};
  border: 1px solid ${({ theme }) => theme.palette.primary.main};

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.light4};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.primary.light3};
  }

  ${disabledButtonStyle$1}
`;
const AlternativeButton = styled(Button$4)`
  color: ${({ theme }) => theme.palette.grayscale.white};
  background-color: #116fab;
  border: 1px solid #116fab;
  &:hover {
    background-color: #1c7cb9;
    border: 1px solid #1c7cb9;
  }

  &:active {
    background-color: #115886;
    border: 1px solid #115886;
  }

  &:focus {
    outline: 2px solid #3db5fd;
  }

  ${disabledButtonStyle$1}
`;
const OkButton = styled(Button$4)`
  color: ${({ theme }) => theme.palette.grayscale.white};
  background-color: ${({ theme }) => theme.palette.success.main};
  border: 1px solid ${({ theme }) => theme.palette.success.main};

  &:hover {
    background-color: ${({ theme }) => theme.palette.success.light1};
    border: 1px solid ${({ theme }) => theme.palette.success.light1};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.success.dark1};
    border: 1px solid ${({ theme }) => theme.palette.success.dark1};
  }

  ${disabledButtonStyle$1}
`;
const DangerButton = styled(Button$4)`
  color: ${({ theme }) => theme.palette.grayscale.white};
  background-color: ${({ theme }) => theme.palette.error.main};
  border: 1px solid ${({ theme }) => theme.palette.error.main};

  &:hover {
    background-color: ${({ theme }) => theme.palette.error.light1};
    border: 1px solid ${({ theme }) => theme.palette.error.light1};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.error.dark1};
    border: 1px solid ${({ theme }) => theme.palette.error.dark1};
  }

  ${disabledButtonStyle$1}
`;
const DownloadButton = styled(Button$4)`
  color: #558cc9;
  background-color: #edf3f9;
  border: 2px dashed #558cc9;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
    background-color: ${({ theme }) => theme.palette.grayscale.white};
    border: 2px dashed ${({ theme }) => theme.palette.primary.main};
  }

  &:active {
    background-color: #edf3f9;
  }

  ${disabledButtonStyle$1}
`;
const SecureButton = styled(Button$4).attrs({
  loadingText: "",
  loader: /* @__PURE__ */ React.createElement(BlinkingBarsLoader, null)
})`
  color: ${({ theme }) => theme.palette.grayscale.white};
  background-color: ${({ theme }) => theme.palette.primary.main};
  border: 1px solid ${({ theme }) => theme.palette.primary.main};

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.light1};
    border: 1px solid ${({ theme }) => theme.palette.primary.light1};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.primary.main};
    border: 1px solid ${({ theme }) => theme.palette.primary.main};
  }

  &:disabled,
  &.loading {
    cursor: not-allowed;
  }

  &:disabled:not(.loading) {
    background-color: ${({ theme }) => theme.palette.disabled.main};
    color: ${({ theme }) => theme.palette.disabled.text};
    border: 1px solid ${({ theme }) => theme.palette.disabled.dark1};

    &:hover {
      background-color: ${({ theme }) => theme.palette.disabled.main};
    }
  }
`;
const DialogCard$2 = styled(StandardCard)`
  border-top: 8px solid ${({ theme }) => theme.palette.primary.main};
  margin: 0;
  max-width: 500px;
`;
const TextSection$2 = styled(Box).attrs(({ theme }) => ({
  direction: "vertical",
  alignX: "center",
  spacing: theme.spacing[2]
}))``;
const ButtonSection$2 = styled(Box).attrs(({ theme }) => ({
  alignX: "center",
  spacing: theme.spacing[4]
}))`
  margin-top: ${Styling.spacing(4)};
`;
const Header$2 = styled.h2`
  ${Styling.typography("h2")};
  color: ${({ theme }) => theme.palette.primary.main};
`;
const Text$4 = styled.div`
  ${Styling.typography("body")};
  color: #888888; // TODO move to theme
`;
const AcknowledgeDialog = ({
  header,
  text,
  okText,
  onOk,
  onClose
}) => {
  const { t } = useTranslation();
  const handleOk = () => {
    onOk == null ? void 0 : onOk();
    onClose == null ? void 0 : onClose();
  };
  return /* @__PURE__ */ React.createElement(DialogCard$2, null, /* @__PURE__ */ React.createElement(TextSection$2, null, /* @__PURE__ */ React.createElement(Header$2, null, header), /* @__PURE__ */ React.createElement(Text$4, null, text)), /* @__PURE__ */ React.createElement(ButtonSection$2, null, /* @__PURE__ */ React.createElement(OkButton, { text: okText ?? t("common.ok"), onClick: handleOk })));
};
const DialogCard$1 = styled(StandardCard)`
  border-top: 8px solid ${({ theme }) => theme.palette.primary.main};
  margin: 0;
  max-width: 500px;
`;
const TextSection$1 = styled(Box).attrs(({ theme }) => ({
  direction: "vertical",
  alignX: "center",
  spacing: theme.spacing[2]
}))``;
const ButtonSection$1 = styled(Box).attrs(({ theme }) => ({
  alignX: "center",
  spacing: theme.spacing[4]
}))`
  margin-top: ${Styling.spacing(4)};
`;
const Header$1 = styled.h2`
  ${Styling.typography("h2")};
  color: ${({ theme }) => theme.palette.primary.main};
`;
const Text$3 = styled.div`
  ${Styling.typography("body")};
  color: #888888; // TODO move to theme
`;
const ConfirmDialog = ({
  header,
  text,
  okText,
  cancelText,
  onOk,
  onCancel,
  onClose
}) => {
  const { t } = useTranslation();
  const handleOk = () => {
    onOk == null ? void 0 : onOk();
    onClose == null ? void 0 : onClose();
  };
  const handleCancel = () => {
    onCancel == null ? void 0 : onCancel();
    onClose == null ? void 0 : onClose();
  };
  return /* @__PURE__ */ React.createElement(DialogCard$1, null, /* @__PURE__ */ React.createElement(TextSection$1, null, /* @__PURE__ */ React.createElement(Header$1, null, header), text && /* @__PURE__ */ React.createElement(Text$3, null, text)), /* @__PURE__ */ React.createElement(ButtonSection$1, null, /* @__PURE__ */ React.createElement(
    DangerButton,
    {
      text: cancelText ?? t("common.reject"),
      onClick: handleCancel
    }
  ), /* @__PURE__ */ React.createElement(OkButton, { text: okText ?? t("common.confirm"), onClick: handleOk })));
};
const DialogCard = styled(StandardCard)`
  border-top: 8px solid ${({ theme }) => theme.palette.error.main};
  margin: 0;
  max-width: 500px;
`;
const TextSection = styled(Box).attrs(({ theme }) => ({
  direction: "vertical",
  alignX: "center",
  spacing: theme.spacing[2]
}))``;
const ButtonSection = styled(Box).attrs(({ theme }) => ({
  direction: "vertical",
  alignX: "center",
  spacing: theme.spacing[1]
}))`
  margin-top: ${Styling.spacing(4)};
`;
const Header = styled.h2`
  ${Styling.typography("h2")};
  color: ${({ theme }) => theme.palette.error.main};
`;
const Text$2 = styled.div`
  ${Styling.typography("body")};
  color: #888888; // TODO move to theme
`;
const FeedbackButton = styled.button.attrs({
  type: "button"
})`
  ${Styling.typography("body")}
  padding: ${Styling.spacing(0, 2)};
  cursor: pointer;
  color: ${({ theme }) => theme.palette.secondary.main};
  background-color: transparent;
  border: none;
  outline: none;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;
const ErrorDialog = ({
  header,
  text,
  okText,
  onOk,
  onClose
}) => {
  const { t } = useTranslation();
  const eventId = useMemo(() => lastEventId(), []);
  const handleFeedback = () => {
    onClose == null ? void 0 : onClose();
    onClose == null ? void 0 : onClose();
    showReportDialog({
      eventId,
      title: t("error.feedback.title"),
      subtitle: t("error.feedback.subtitle"),
      subtitle2: t("error.feedback.subtitle2"),
      labelName: t("error.feedback.labelName"),
      labelEmail: t("error.feedback.labelEmail"),
      labelComments: t("error.feedback.labelComments"),
      labelClose: t("error.feedback.labelClose"),
      labelSubmit: t("error.feedback.labelSubmit"),
      errorGeneric: t("error.feedback.errorGeneric"),
      errorFormEntry: t("error.feedback.errorFormEntry"),
      successMessage: t("error.feedback.successMessage")
    });
  };
  const handleOk = () => {
    onOk == null ? void 0 : onOk();
    onClose == null ? void 0 : onClose();
  };
  return /* @__PURE__ */ React.createElement(DialogCard, null, /* @__PURE__ */ React.createElement(TextSection, null, /* @__PURE__ */ React.createElement(Header, null, header ?? t("errors.an_error_occurred")), /* @__PURE__ */ React.createElement(Text$2, null, text)), /* @__PURE__ */ React.createElement(ButtonSection, null, /* @__PURE__ */ React.createElement(DangerButton, { text: okText ?? t("common.ok"), onClick: handleOk }), eventId && /* @__PURE__ */ React.createElement(FeedbackButton, { onClick: handleFeedback }, t("error.feedback.button"))));
};
var faCalendar = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "calendar";
  var width = 448;
  var height = 512;
  var aliases = [128197, 128198];
  var unicode = "f133";
  var svgPathData = "M96 32V64H48C21.5 64 0 85.5 0 112v48H448V112c0-26.5-21.5-48-48-48H352V32c0-17.7-14.3-32-32-32s-32 14.3-32 32V64H160V32c0-17.7-14.3-32-32-32S96 14.3 96 32zM448 192H0V464c0 26.5 21.5 48 48 48H400c26.5 0 48-21.5 48-48V192z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faCalendar = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faCalendar);
const commonDatePickerStyle = css`
  & .react-date-picker {
    width: 100%;
    height: 53px;
    border-radius: ${Styling.radii("input")};
    ${Styling.typography("input")}

    & .react-date-picker__wrapper {
      padding: ${Styling.spacing(1.5, 2)};
      border: 0;
    }

    &--disabled .react-date-picker__inputGroup {
      cursor: not-allowed !important;
    }

    & .react-date-picker__inputGroup {
      cursor: pointer;
    }

    & .react-date-picker__calendar-button {
      margin-left: ${Styling.spacing(1.5)};
      color: ${({ theme }) => theme.palette.primary.main};

      &:focus {
        outline: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }

    & .react-calendar {
      padding: ${Styling.spacing(1.5, 2)};
      border: ${({ theme }) => `1px solid ${theme.palette.grayscale.light2}`};
      border-radius: ${Styling.radii("input")};
      box-shadow: ${Styling.shadow("dropdown")};

      & .react-calendar__tile--now {
        background-color: initial;
      }

      & .react-calendar__tile--active {
        background-color: ${({ theme }) => theme.palette.primary.main};
      }

      & .react-calendar__tile--hasActive {
        background-color: ${({ theme }) => theme.palette.primary.main};
      }
    }
  }
`;
const RoundDatePickerBase = styled.div`
  & .react-date-picker {
    color: ${({ theme }) => theme.palette.primary.text};
    background-color: ${({ theme }) => theme.palette.grayscale.white};
    border: 0;

    &:hover {
      outline: ${({ theme }) => `1px solid ${theme.palette.grayscale.light2}`};
    }

    &--open {
      outline: ${({ theme }) => `1px solid ${theme.palette.primary.main} !important`};
    }

    &--disabled {
      background-color: rgba(255, 255, 255, 0.5);
    }

    & .react-date-picker__inputGroup > input {
      color: ${({ theme }) => theme.palette.primary.text};

      &:focus {
        outline: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }
  }

  ${commonDatePickerStyle}
`;
const FilledDatePickerBase = styled.div`
  & .react-date-picker {
    color: ${({ theme }) => theme.palette.primary.text};
    background-color: #f9fafb;
    border: 1px solid #e9e9e9;
    transition:
      100ms background-color,
      100ms border;

    &:hover {
      background-color: ${({ theme }) => theme.palette.grayscale.light5};
      outline: ${({ theme }) => `1px solid ${theme.palette.grayscale.light2}`};
    }

    &--open {
      outline: ${({ theme }) => `1px solid ${theme.palette.primary.main} !important`};
    }

    &--disabled {
      background-color: #f9fafb;
      border: ${({ theme }) => `1px solid ${theme.palette.grayscale.light4}`};
    }

    & .react-date-picker__inputGroup > input {
      color: ${({ theme }) => theme.palette.primary.text};

      &:focus {
        outline: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }
  }

  ${commonDatePickerStyle}
`;
const DateInput = ({
  name,
  value,
  minDate,
  maxDate,
  onChange,
  disabled = false
}) => {
  const { i18n } = useTranslation();
  return /* @__PURE__ */ React.createElement(
    DatePicker,
    {
      name,
      format: "y-MM-dd",
      locale: i18n.language,
      disabled,
      value,
      onChange: (value2) => onChange == null ? void 0 : onChange(value2),
      minDetail: "decade",
      minDate,
      maxDate,
      calendarIcon: /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faCalendar.faCalendar, size: "lg" }),
      clearIcon: null
    }
  );
};
const RoundDateInput = ({ className, ...props }) => {
  return /* @__PURE__ */ React.createElement(RoundDatePickerBase, { className }, /* @__PURE__ */ React.createElement(DateInput, { ...props }));
};
const FilledDateInput = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ React.createElement(FilledDatePickerBase, { className }, /* @__PURE__ */ React.createElement(DateInput, { ...props }));
};
const commonDateTimePickerStyle = css`
  & .react-datetime-picker {
    width: 100%;
    height: 53px;
    border-radius: ${Styling.radii("input")};
    ${Styling.typography("input")}

    & .react-datetime-picker__wrapper {
      padding: ${Styling.spacing(1.5, 2)};
      border: 0;
    }

    &--disabled .react-datetime-picker__inputGroup {
      cursor: not-allowed !important;
    }

    & .react-datetime-picker__inputGroup {
      cursor: pointer;
    }

    & .react-datetime-picker__button {
      margin-left: ${Styling.spacing(1.5)};
      color: ${({ theme }) => theme.palette.primary.main};

      &:focus {
        outline: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }

    & .react-calendar {
      padding: ${Styling.spacing(1.5, 2)};
      border: ${({ theme }) => `1px solid ${theme.palette.grayscale.light2}`};
      border-radius: ${Styling.radii("input")};
      box-shadow: ${Styling.shadow("dropdown")};

      & .react-calendar__tile--now {
        background-color: initial;
      }

      & .react-calendar__tile--active {
        background-color: ${({ theme }) => theme.palette.primary.main};
      }

      & .react-calendar__tile--hasActive {
        background-color: ${({ theme }) => theme.palette.primary.main};
      }
    }
  }
`;
const RoundDateTimePickerBase = styled.div`
  & .react-datetime-picker {
    color: ${({ theme }) => theme.palette.primary.text};
    background-color: ${({ theme }) => theme.palette.grayscale.white};
    border: 0;

    &:hover {
      outline: ${({ theme }) => `1px solid ${theme.palette.grayscale.light2}`};
    }

    &--open {
      outline: ${({ theme }) => `1px solid ${theme.palette.primary.main} !important`};
    }

    &--disabled {
      background-color: rgba(255, 255, 255, 0.5);
    }

    & .react-datetime-picker__inputGroup > input {
      color: ${({ theme }) => theme.palette.primary.text};

      &:focus {
        outline: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }
  }

  ${commonDateTimePickerStyle}
`;
const FilledDateTimePickerBase = styled.div`
  & .react-datetime-picker {
    color: ${({ theme }) => theme.palette.primary.text};
    background-color: #f9fafb;
    border: 1px solid #e9e9e9;
    transition:
      100ms background-color,
      100ms border;

    &:hover {
      background-color: ${({ theme }) => theme.palette.grayscale.light5};
      outline: ${({ theme }) => `1px solid ${theme.palette.grayscale.light2}`};
    }

    &--open {
      outline: ${({ theme }) => `1px solid ${theme.palette.primary.main} !important`};
    }

    &--disabled {
      background-color: #f9fafb;
      border: ${({ theme }) => `1px solid ${theme.palette.grayscale.light4}`};
    }

    & .react-datetime-picker__inputGroup > input {
      color: ${({ theme }) => theme.palette.primary.text};

      &:focus {
        outline: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }
  }

  ${commonDateTimePickerStyle}
`;
const DateTimeInput = ({
  name,
  value,
  minDate,
  maxDate,
  onChange,
  disabled = false
}) => {
  const { i18n } = useTranslation();
  return /* @__PURE__ */ React.createElement(
    DateTimePicker,
    {
      name,
      format: "y-MM-dd HH:mm",
      locale: i18n.language,
      disabled,
      value,
      onChange: (date) => onChange == null ? void 0 : onChange(date ?? /* @__PURE__ */ new Date()),
      minDetail: "decade",
      minDate,
      maxDate,
      calendarIcon: /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faCalendar.faCalendar, size: "lg" }),
      clearIcon: null
    }
  );
};
const RoundDateTimeInput = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ React.createElement(RoundDateTimePickerBase, { className }, /* @__PURE__ */ React.createElement(DateTimeInput, { ...props }));
};
const FilledDateTimeInput = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ React.createElement(FilledDateTimePickerBase, { className }, /* @__PURE__ */ React.createElement(DateTimeInput, { ...props }));
};
var faXmark = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "xmark";
  var width = 384;
  var height = 512;
  var aliases = [128473, 10005, 10006, 10060, 215, "close", "multiply", "remove", "times"];
  var unicode = "f00d";
  var svgPathData = "M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faXmark = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faXmark);
var faPaperclip = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "paperclip";
  var width = 448;
  var height = 512;
  var aliases = [128206];
  var unicode = "f0c6";
  var svgPathData = "M364.2 83.8c-24.4-24.4-64-24.4-88.4 0l-184 184c-42.1 42.1-42.1 110.3 0 152.4s110.3 42.1 152.4 0l152-152c10.9-10.9 28.7-10.9 39.6 0s10.9 28.7 0 39.6l-152 152c-64 64-167.6 64-231.6 0s-64-167.6 0-231.6l184-184c46.3-46.3 121.3-46.3 167.6 0s46.3 121.3 0 167.6l-176 176c-28.6 28.6-75 28.6-103.6 0s-28.6-75 0-103.6l144-144c10.9-10.9 28.7-10.9 39.6 0s10.9 28.7 0 39.6l-144 144c-6.7 6.7-6.7 17.7 0 24.4s17.7 6.7 24.4 0l176-176c24.4-24.4 24.4-64 0-88.4z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faPaperclip = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faPaperclip);
const Container$c = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Text$1 = styled.span`
  position: absolute;
  padding: 8px 12px;
  color: ${({ theme }) => theme.palette.grayscale.main};
  background-color: ${({ theme }) => theme.palette.grayscale.white};
`;
const HorizontalHr = styled.hr`
  background: ${({ theme }) => theme.palette.grayscale.light4};
  height: 1px;
  width: 100%;
  margin: 0;
  border: 0;
`;
const VerticalHr = styled.hr`
  background: ${({ theme }) => theme.palette.grayscale.light4};
  width: 1px;
  margin: 0;
  border: 0;
`;
const Divider = ({
  className,
  orientation,
  children
}) => {
  const element = orientation === "horizontal" ? /* @__PURE__ */ React.createElement(HorizontalHr, { className }) : /* @__PURE__ */ React.createElement(VerticalHr, { className });
  if (children) {
    return /* @__PURE__ */ React.createElement(Container$c, null, element, children && /* @__PURE__ */ React.createElement(Text$1, null, children));
  }
  return element;
};
const Container$b = styled.div``;
const DropArea = styled.div`
  position: relative;
  width: 100%;
  height: 100px;
  padding: ${Styling.spacing(2)};
  border: 2px dashed
    ${({ theme, isDragging }) => isDragging ? theme.palette.primary.main : "#E9E9E9"};
  background-color: ${({ theme, isDragging }) => isDragging ? theme.palette.primary.light5 : theme.palette.grayscale.light5};
  transition:
    100ms background-color,
    100ms border;

  &:hover {
    border: 2px dashed
      ${({ theme, isDragging }) => isDragging ? theme.palette.primary.main : theme.palette.grayscale.light2};
    background-color: ${({ theme, isDragging }) => isDragging ? theme.palette.primary.light5 : theme.palette.grayscale.light5};
  }
`;
const FileInput = styled.input.attrs({
  type: "file"
})`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;
const Icon = styled(FontAwesomeIcon).attrs({
  size: "lg"
})`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 100%;
  max-height: 42px;
  color: ${({ theme }) => theme.palette.grayscale.light1};
`;
const Label$1 = styled.div`
  ${Styling.typography("h5")};
  color: ${({ theme }) => theme.palette.grayscale.light1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const FileList = styled(Box).attrs({
  direction: "vertical"
})``;
const FileItem = styled(Box).attrs({
  alignX: "space-between",
  alignY: "center"
})`
  padding: ${Styling.spacing(1.5)};
  background-color: white;

  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale.light5};
  }
`;
const FileName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const RemoveButton = styled.button.attrs({
  type: "button"
})`
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  transition:
    color 80ms,
    background-color 80ms;
  height: 24px;
  width: 24px;
  color: #a5a5a5; // TODO move to theme
  background-color: ${({ theme }) => theme.palette.disabled.main};

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
    background-color: ${({ theme }) => theme.palette.grayscale.white};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.disabled.main};
    color: #a5a5a5; // TODO move to theme
  }
`;
const FileDropArea = ({
  className,
  name,
  disabled,
  multiple,
  accept,
  files,
  onChange,
  icon,
  label
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => {
    if (files.length === 0) {
      return;
    }
    let dt;
    try {
      dt = new DataTransfer();
    } catch (e) {
      return;
    }
    for (let i = 0; i < files.length; i++) {
      dt.items.add(files[i]);
    }
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.files = dt.files;
    }
  }, []);
  const handleChange = (evt) => {
    const files2 = [];
    const fileList = evt.target.files;
    if (fileList) {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList.item(i);
        if (file) {
          files2.push(file);
        }
      }
    }
    onChange == null ? void 0 : onChange(files2);
  };
  const handleDrop = (evt) => {
    evt.preventDefault();
    const files2 = Array.from(evt.dataTransfer.files);
    if (!multiple && files2.length > 0) {
      onChange == null ? void 0 : onChange([files2[0]]);
    } else {
      onChange == null ? void 0 : onChange(files2);
    }
    setIsDragging(false);
  };
  const handleRemove = (index) => {
    const copy = files.slice();
    copy.splice(index, 1);
    onChange == null ? void 0 : onChange(copy);
  };
  return /* @__PURE__ */ React.createElement(Container$b, null, /* @__PURE__ */ React.createElement(
    DropArea,
    {
      className,
      isDragging,
      onDragStart: () => setIsDragging(true),
      onDragEnd: () => setIsDragging(false),
      onDragEnter: () => setIsDragging(true),
      onDragLeave: () => setIsDragging(false),
      onDragOver: () => setIsDragging(true),
      onDrop: handleDrop
    },
    /* @__PURE__ */ React.createElement(Label$1, null, label),
    /* @__PURE__ */ React.createElement(Icon, { icon: icon ?? faPaperclip.faPaperclip }),
    /* @__PURE__ */ React.createElement(
      FileInput,
      {
        ref: inputRef,
        name,
        disabled,
        multiple,
        accept: accept == null ? void 0 : accept.join(", "),
        onChange: handleChange
      }
    )
  ), files.length > 0 && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FileList, null, files.map((file, index) => /* @__PURE__ */ React.createElement(FileItem, { key: file.name }, /* @__PURE__ */ React.createElement(FileName, null, file.name), /* @__PURE__ */ React.createElement(RemoveButton, { onClick: () => handleRemove(index) }, /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faXmark.faXmark, size: "lg" }))))), /* @__PURE__ */ React.createElement(Divider, { orientation: "horizontal" })));
};
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const LoaderWrapper = styled.div`
  position: relative;
  height: 0;
  top: -12px;
  animation: ${fadeIn} 200ms linear 1;
`;
const Loader = () => {
  const theme = useTheme();
  return /* @__PURE__ */ React.createElement(LoaderWrapper, null, /* @__PURE__ */ React.createElement(
    TailSpin,
    {
      width: `24px`,
      height: `24px`,
      color: theme.palette.primary.main,
      ariaLabel: "loading-indicator"
    }
  ));
};
const IconButtonBase = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: ${Styling.radii("button")};
  cursor: pointer;
  transition:
    color 80ms,
    background-color 80ms;
  height: 36px;
  width: 36px;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
  }
`;
const IconButton = ({
  className = "",
  loading,
  disabled,
  icon,
  ...props
}) => {
  if (loading) {
    className += " loading";
  }
  return /* @__PURE__ */ React.createElement(
    IconButtonBase,
    {
      className,
      disabled: loading || disabled,
      ...props
    },
    loading ? /* @__PURE__ */ React.createElement(Loader, null) : icon
  );
};
const disabledButtonStyle = css(({ theme }) => ({
  "&:disabled": {
    cursor: "not-allowed",
    backgroundColor: theme.palette.disabled.main,
    color: theme.palette.secondary.light3,
    "&:hover": {
      backgroundColor: theme.palette.disabled.main
    }
  }
}));
const PrimaryActionIconButton = styled(IconButton)(
  ({ theme }) => ({
    color: theme.palette.primary.light1,
    backgroundColor: "#fff3e5",
    // TODO move to theme
    "&:hover": {
      backgroundColor: "#FFEAC8"
      // TODO move to theme
    },
    "&:active": {
      backgroundColor: theme.palette.disabled.main,
      color: "#A5A5A5"
      // TODO move to theme
    },
    "&:focus": {
      outline: `2px solid ${theme.palette.primary.main}`
    }
  }),
  disabledButtonStyle
);
const AlternativeActionIconButton = styled(IconButton)(
  ({ theme }) => ({
    color: "#3d97e5",
    // TODO move to theme
    backgroundColor: "#e5f2ff",
    // TODO move to theme
    "&:hover": {
      backgroundColor: "#c8e6ff"
      // TODO move to theme
    },
    "&:active": {
      backgroundColor: theme.palette.disabled.main,
      color: "#A5A5A5"
      // TODO move to theme
    },
    "&:focus": {
      outline: "2px solid #6ab1f3"
      // TODO move to theme
    }
  }),
  disabledButtonStyle
);
const DangerActionIconButton = styled(IconButton)(
  ({ theme }) => ({
    color: theme.palette.error.main,
    backgroundColor: "#F1E9E9",
    // TODO move to theme
    "&:hover": {
      backgroundColor: "#f6d7d7"
      // TODO move to theme
    },
    "&:active": {
      backgroundColor: theme.palette.disabled.main,
      color: "#A5A5A5"
      // TODO move to theme
    },
    "&:focus": {
      outline: `2px solid ${theme.palette.error.light1}`
    }
  }),
  disabledButtonStyle
);
const CircleIconButton = styled(IconButton)(
  ({ theme }) => ({
    color: theme.palette.grayscale.main,
    borderRadius: "50%",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: theme.palette.grayscale.light5
    }
  }),
  disabledButtonStyle
);
var faLock = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "lock";
  var width = 448;
  var height = 512;
  var aliases = [128274];
  var unicode = "f023";
  var svgPathData = "M144 144v48H304V144c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192V144C80 64.5 144.5 0 224 0s144 64.5 144 144v48h16c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V256c0-35.3 28.7-64 64-64H80z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faLock = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faLock);
var faEyeSlash = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "eye-slash";
  var width = 640;
  var height = 512;
  var aliases = [];
  var unicode = "f070";
  var svgPathData = "M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faEyeSlash = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faEyeSlash);
var faEye = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "eye";
  var width = 576;
  var height = 512;
  var aliases = [128065];
  var unicode = "f06e";
  var svgPathData = "M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faEye = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faEye);
const RoundTextFieldBase = styled.div`
  width: 100%;
  height: 53px;
  display: flex;
  align-items: center;
  position: relative;
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "auto"};
  color: ${({ theme }) => theme.palette.primary.text};
  background-color: ${({ theme, disabled }) => disabled ? "rgba(255, 255, 255, 0.5)" : theme.palette.grayscale.white};
  border-radius: ${Styling.radii("input")};
  border: ${({ theme, focus, valid }) => {
  if (focus) {
    return "none";
  }
  if (valid === true) {
    return `2px solid ${theme.palette.success.main} !important`;
  } else if (valid === false) {
    return `2px solid ${theme.palette.error.main} !important`;
  } else {
    return "none";
  }
}};
  outline: ${({ theme, focus }) => focus ? `1px solid ${theme.palette.primary.main}` : 0};

  &:hover {
    outline: ${({ theme, focus }) => focus ? void 0 : `1px solid ${theme.palette.grayscale.light2}`};
  }
`;
const UnderlinedTextFieldBase = styled.div`
  width: 100%;
  height: 53px;
  display: flex;
  align-items: center;
  position: relative;
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "auto"};
  color: ${({ theme }) => theme.palette.primary.text};
  background-color: ${({ theme, disabled }) => disabled ? theme.palette.grayscale.light5 : theme.palette.grayscale.white};
  border-top-left-radius: ${Styling.radii("input")};
  border-top-right-radius: ${Styling.radii("input")};
  border-bottom: ${({ theme, valid }) => {
  if (valid === true) {
    return `2px solid ${theme.palette.success.main} !important`;
  } else if (valid === false) {
    return `2px solid ${theme.palette.error.main} !important`;
  } else {
    return `2px solid ${theme.palette.grayscale.light5}`;
  }
}};

  &:hover {
    border-bottom: ${({ theme, disabled }) => disabled ? void 0 : `2px solid ${theme.palette.grayscale.light4}`};
  }
`;
const FilledTextFieldBase = styled.div`
  width: 100%;
  height: 53px;
  display: flex;
  align-items: center;
  position: relative;
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "auto"};
  color: ${({ theme }) => theme.palette.primary.text};
  background-color: ${({ theme, disabled }) => disabled ? theme.palette.grayscale.light5 + "7F" : theme.palette.grayscale.light5};
  border-radius: ${Styling.radii("input")};
  outline: ${({ theme, focus }) => focus ? `1px solid ${theme.palette.primary.main}` : 0};
  transition: 100ms border;

  &:hover {
    outline: ${({ theme, focus }) => focus ? void 0 : `1px solid ${theme.palette.grayscale.light2}`};
  }
`;
const TextFieldInput = styled.input`
  ${Styling.typography("input")}
  padding: ${Styling.spacing(2)};
  width: 100%;
  border: 0;
  outline: 0;
  background-color: transparent;
  color: inherit;

  &:disabled {
    cursor: not-allowed;
  }

  // Hack to hide Chrome's autofill styling
  &:-webkit-autofill,
  &:-webkit-autofill:active,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px ${Styling.prop("backgroundColor")} inset;
  }
`;
const Adornment = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  color: ${({ theme }) => theme.palette.grayscale.main};
`;
const StartAdornment = styled(Adornment)`
  flex-shrink: 0;
  margin-left: ${Styling.spacing(2)};
`;
const EndAdornment = styled(Adornment)`
  flex-shrink: 0;
  margin-right: ${Styling.spacing(2)};
`;
const RoundTextField = ({
  className,
  startAdornment,
  endAdornment,
  valid,
  innerRef,
  ...props
}) => {
  const theme = useTheme();
  const [hasFocus, setHasFocus] = useState(false);
  const ref = useRef(null);
  const backgroundColor = ref.current ? window.getComputedStyle(ref.current).backgroundColor : theme.palette.grayscale.light5;
  return /* @__PURE__ */ React.createElement(
    RoundTextFieldBase,
    {
      ref,
      disabled: props.disabled,
      valid,
      focus: hasFocus,
      className
    },
    startAdornment && /* @__PURE__ */ React.createElement(StartAdornment, null, startAdornment),
    /* @__PURE__ */ React.createElement(
      TextFieldInput,
      {
        ref: innerRef,
        type: "text",
        backgroundColor,
        startAdornment: Boolean(startAdornment),
        endAdornment: Boolean(endAdornment),
        ...props,
        onFocus: (evt) => {
          var _a;
          setHasFocus(true);
          (_a = props.onFocus) == null ? void 0 : _a.call(props, evt);
        },
        onBlur: (evt) => {
          var _a;
          setHasFocus(false);
          (_a = props.onBlur) == null ? void 0 : _a.call(props, evt);
        }
      }
    ),
    endAdornment && /* @__PURE__ */ React.createElement(EndAdornment, null, endAdornment)
  );
};
const Underline$1 = styled.div`
  width: ${({ visible }) => visible ? "100%" : "0%"};
  height: 2px;
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${({ theme }) => theme.palette.primary.main};
  transition: width 120ms;
`;
const UnderlinedTextField = ({
  className,
  startAdornment,
  endAdornment,
  valid,
  innerRef,
  ...props
}) => {
  const [hasFocus, setHasFocus] = useState(false);
  const ref = useRef(null);
  const backgroundColor = ref.current ? window.getComputedStyle(ref.current).backgroundColor : "white";
  return /* @__PURE__ */ React.createElement(
    UnderlinedTextFieldBase,
    {
      ref,
      disabled: props.disabled,
      valid,
      focus: hasFocus,
      className
    },
    startAdornment && /* @__PURE__ */ React.createElement(StartAdornment, null, startAdornment),
    /* @__PURE__ */ React.createElement(
      TextFieldInput,
      {
        ref: innerRef,
        type: "text",
        backgroundColor,
        startAdornment: Boolean(startAdornment),
        endAdornment: Boolean(endAdornment),
        ...props,
        onFocus: (evt) => {
          var _a;
          setHasFocus(true);
          (_a = props.onFocus) == null ? void 0 : _a.call(props, evt);
        },
        onBlur: (evt) => {
          var _a;
          setHasFocus(false);
          (_a = props.onBlur) == null ? void 0 : _a.call(props, evt);
        }
      }
    ),
    endAdornment && /* @__PURE__ */ React.createElement(EndAdornment, null, endAdornment),
    /* @__PURE__ */ React.createElement(Underline$1, { visible: hasFocus })
  );
};
const FilledTextField = ({
  className,
  startAdornment,
  endAdornment,
  valid,
  innerRef,
  ...props
}) => {
  const theme = useTheme();
  const [hasFocus, setHasFocus] = useState(false);
  const ref = useRef(null);
  const backgroundColor = ref.current ? window.getComputedStyle(ref.current).backgroundColor : theme.palette.grayscale.light5;
  return /* @__PURE__ */ React.createElement(
    FilledTextFieldBase,
    {
      ref,
      disabled: props.disabled,
      valid,
      focus: hasFocus,
      className
    },
    startAdornment && /* @__PURE__ */ React.createElement(StartAdornment, null, startAdornment),
    /* @__PURE__ */ React.createElement(
      TextFieldInput,
      {
        ref: innerRef,
        type: "text",
        backgroundColor,
        startAdornment: Boolean(startAdornment),
        endAdornment: Boolean(endAdornment),
        ...props,
        onFocus: (evt) => {
          var _a;
          setHasFocus(true);
          (_a = props.onFocus) == null ? void 0 : _a.call(props, evt);
        },
        onBlur: (evt) => {
          var _a;
          setHasFocus(false);
          (_a = props.onBlur) == null ? void 0 : _a.call(props, evt);
        }
      }
    ),
    endAdornment && /* @__PURE__ */ React.createElement(EndAdornment, null, endAdornment)
  );
};
const UnderlinedHidePasswordButton = styled(CircleIconButton).attrs({
  type: "button"
})``;
const FilledHidePasswordButton = styled(CircleIconButton).attrs({
  type: "button"
})`
  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale.light4};
  }
`;
const PasswordField = ({
  TextField,
  ToggleButton,
  autoComplete = "off",
  placeholder,
  ...props
}) => {
  const [isHovering, setIsHovering] = useState(0);
  const [hidden, setHidden] = useState(1);
  const toggleHidden = () => setHidden((hidden2) => hidden2 ? 0 : 1);
  return /* @__PURE__ */ React.createElement(
    TextField,
    {
      type: hidden ? "password" : "text",
      autoComplete,
      startAdornment: /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faLock.faLock, size: "lg" }),
      endAdornment: /* @__PURE__ */ React.createElement(
        ToggleButton,
        {
          "aria-label": hidden ? "Show password" : "Hide password",
          onMouseEnter: () => setIsHovering(1),
          onMouseLeave: () => setIsHovering(0),
          disabled: props.disabled,
          onClick: toggleHidden,
          icon: /* @__PURE__ */ React.createElement(
            FontAwesomeIcon,
            {
              icon: isHovering ^ hidden ? faEyeSlash.faEyeSlash : faEye.faEye,
              size: "lg"
            }
          )
        }
      ),
      placeholder: hidden ? "●".repeat((placeholder == null ? void 0 : placeholder.length) ?? 8) : placeholder,
      valid: void 0,
      ...props
    }
  );
};
const UnderlinedPasswordField = (props) => /* @__PURE__ */ React.createElement(
  PasswordField,
  {
    ...props,
    TextField: UnderlinedTextField,
    ToggleButton: UnderlinedHidePasswordButton
  }
);
const FilledPasswordField = (props) => /* @__PURE__ */ React.createElement(
  PasswordField,
  {
    ...props,
    TextField: FilledTextField,
    ToggleButton: FilledHidePasswordButton
  }
);
const Label = styled.label`
  ${Styling.typography("body")}
  color: ${({ theme }) => theme.palette.primary.text};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => flexGap(theme.spacing[1])};
  cursor: pointer;
  user-select: none;

  &.disabled {
    cursor: not-allowed;
  }
`;
const Input = styled.input.attrs({
  type: "radio"
})`
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
`;
const Dot = styled.span(({ hover, checked, disabled, theme }) => ({
  position: "relative",
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  height: "24px",
  width: "24px",
  backgroundColor: theme.palette.grayscale.white,
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: "50%",
  ...hover ? {
    backgroundColor: theme.palette.primary.light5
  } : {},
  ...checked ? {
    backgroundColor: theme.palette.grayscale.white,
    "&:after": {
      content: '""',
      position: "absolute",
      backgroundColor: disabled ? theme.palette.primary.light2 : theme.palette.primary.main,
      width: "14px",
      height: "14px",
      borderRadius: "50%"
    }
  } : {},
  ...disabled ? {
    backgroundColor: theme.palette.grayscale.light4,
    border: `1px solid ${theme.palette.grayscale.light3}`
  } : {}
}));
const RadioButton = ({
  className,
  name,
  checked,
  disabled,
  onChange,
  label
}) => {
  const [isHovering, setIsHovering] = useState(false);
  if (disabled) {
    className += " disabled";
  }
  return /* @__PURE__ */ React.createElement(
    Label,
    {
      className,
      onMouseEnter: () => setIsHovering(true),
      onMouseLeave: () => setIsHovering(false)
    },
    /* @__PURE__ */ React.createElement(Dot, { hover: isHovering, checked, disabled }),
    label,
    /* @__PURE__ */ React.createElement(
      Input,
      {
        name,
        disabled,
        checked,
        onChange: (evt) => onChange == null ? void 0 : onChange(evt.target.checked)
      }
    )
  );
};
const FieldSet = styled.fieldset`
  margin: 0;
  padding: 0;
  display: flex;
  align-items: baseline;
  flex-direction: ${({ direction }) => direction === "horizontal" ? "row" : "column"};
  ${({ theme }) => flexGap(theme.spacing[2])};
  border: 0;
`;
const RadioGroup = ({
  name,
  disabled,
  value,
  direction = "horizontal",
  options,
  onChange
}) => {
  const handleChange = (option, selected) => {
    if (selected) {
      onChange == null ? void 0 : onChange(option);
    }
  };
  return /* @__PURE__ */ React.createElement(FieldSet, { direction }, options.map((option) => /* @__PURE__ */ React.createElement(
    RadioButton,
    {
      key: option.value,
      name,
      checked: option.value === value,
      disabled: option.disabled || disabled,
      onChange: (selected) => handleChange(option.value, selected),
      label: option.label
    }
  )));
};
var faArrowsRotate = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "arrows-rotate";
  var width = 512;
  var height = 512;
  var aliases = [128472, "refresh", "sync"];
  var unicode = "f021";
  var svgPathData = "M105.1 202.6c7.7-21.8 20.2-42.3 37.8-59.8c62.5-62.5 163.8-62.5 226.3 0L386.3 160H352c-17.7 0-32 14.3-32 32s14.3 32 32 32H463.5c0 0 0 0 0 0h.4c17.7 0 32-14.3 32-32V80c0-17.7-14.3-32-32-32s-32 14.3-32 32v35.2L414.4 97.6c-87.5-87.5-229.3-87.5-316.8 0C73.2 122 55.6 150.7 44.8 181.4c-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5zM39 289.3c-5 1.5-9.8 4.2-13.7 8.2c-4 4-6.7 8.8-8.1 14c-.3 1.2-.6 2.5-.8 3.8c-.3 1.7-.4 3.4-.4 5.1V432c0 17.7 14.3 32 32 32s32-14.3 32-32V396.9l17.6 17.5 0 0c87.5 87.4 229.3 87.4 316.7 0c24.4-24.4 42.1-53.1 52.9-83.7c5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8c-62.5 62.5-163.8 62.5-226.3 0l-.1-.1L125.6 352H160c17.7 0 32-14.3 32-32s-14.3-32-32-32H48.4c-1.6 0-3.2 .1-4.8 .3s-3.1 .5-4.6 1z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faArrowsRotate = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faArrowsRotate);
function generateRandomString(length, characters = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz~!@-_*#$.") {
  return Array.from(crypto.getRandomValues(new Uint32Array(length))).map((x) => characters[x % characters.length]).join("");
}
const RegenerateButton = styled(AlternativeActionIconButton).attrs({
  type: "button"
})(({ theme }) => ({
  color: theme.palette.grayscale.main,
  backgroundColor: theme.palette.grayscale.light5,
  "&:hover": {
    backgroundColor: theme.palette.grayscale.light4
  },
  "&:active": {
    backgroundColor: theme.palette.disabled.main,
    color: theme.palette.grayscale.light1
  },
  "&:focus": {
    outline: `2px solid ${theme.palette.primary.main}`
  }
}));
const RandomField = ({
  PasswordField: PasswordField2,
  minLength = 15,
  maxLength = 30,
  ...props
}) => {
  const ref = useRef(null);
  const handleGenerate = () => {
    var _a, _b;
    const input = ref.current;
    if (input) {
      const length = minLength + Math.floor(Math.random() * clamp(maxLength - minLength, 0, maxLength));
      const value = generateRandomString(length);
      (_b = (_a = Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        "value"
      )) == null ? void 0 : _a.set) == null ? void 0 : _b.call(input, value);
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };
  return /* @__PURE__ */ React.createElement(Box, { direction: "horizontal", alignY: "center", spacing: 12 }, /* @__PURE__ */ React.createElement(PasswordField2, { ...props, innerRef: ref }), /* @__PURE__ */ React.createElement(
    RegenerateButton,
    {
      "aria-label": "Regenerate",
      disabled: props.disabled,
      onClick: handleGenerate,
      icon: /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faArrowsRotate.faArrowsRotate, size: "lg" })
    }
  ));
};
const UnderlinedRandomField = (props) => {
  return /* @__PURE__ */ React.createElement(RandomField, { PasswordField: UnderlinedPasswordField, ...props });
};
const FilledRandomField = (props) => {
  return /* @__PURE__ */ React.createElement(RandomField, { PasswordField: FilledPasswordField, ...props });
};
const dropdownRoot = document.getElementById("dropdown-root");
function getFilledSelectStyles(theme) {
  return {
    container: (provided) => ({
      ...provided,
      pointerEvents: "all",
      fontFamily: theme.typography.input.fontFamily,
      fontSize: theme.typography.input.fontSize,
      fontWeight: theme.typography.input.fontWeight,
      letterSpacing: theme.typography.input.letterSpacing,
      lineHeight: theme.typography.input.lineHeight,
      width: "100%"
    }),
    menu: (provided) => ({
      ...provided,
      width: "max-content",
      minWidth: "100%",
      margin: 0,
      borderRadius: `0 0 ${theme.radii.input} ${theme.radii.input}`,
      zIndex: theme.zIndex.dropdown
    }),
    option: (provided, state) => ({
      ...provided,
      padding: `${theme.spacing[1.5]} ${theme.spacing[1.5]}`,
      color: theme.palette.primary.text,
      backgroundColor: state.isFocused ? theme.palette.grayscale.light5 : "transparent"
    }),
    control: (provided, state) => ({
      ...provided,
      height: "53px",
      flexWrap: "nowrap",
      padding: `${theme.spacing[0.75]} ${theme.spacing[0.5]}`,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      backgroundColor: theme.palette.grayscale.light5,
      outline: state.isFocused ? `1px solid ${theme.palette.primary.main}` : "1px solid #E9E9E9",
      // TODO move to theme
      border: 0,
      borderRadius: state.menuIsOpen ? `${theme.radii.input} ${theme.radii.input} 0 0` : theme.radii.input,
      boxShadow: "none",
      transition: "none",
      "&:hover": {
        outline: state.isFocused ? `1px solid ${theme.palette.primary.main}` : "1px solid #B3B3B3",
        backgroundColor: state.isDisabled ? "#F9FAFB" : theme.palette.grayscale.light5
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: theme.palette.primary.text
    }),
    input: (provided) => ({
      ...provided,
      color: theme.palette.primary.text
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme.palette.primary.text
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0px 4px"
    }),
    multiValue: (provided) => ({
      ...provided,
      border: "1px solid #E9E9E9",
      padding: "4px 0px"
    }),
    indicatorSeparator: () => ({
      display: "none"
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: theme.palette.primary.main,
      "&:hover": {
        color: theme.palette.primary.main
      }
    })
  };
}
function getRoundSelectStyles(theme) {
  return {
    container: (provided) => ({
      ...provided,
      pointerEvents: "all",
      fontFamily: theme.typography.input.fontFamily,
      fontSize: theme.typography.input.fontSize,
      fontWeight: theme.typography.input.fontWeight,
      letterSpacing: theme.typography.input.letterSpacing,
      lineHeight: theme.typography.input.lineHeight,
      width: "100%"
    }),
    menu: (provided) => ({
      ...provided,
      width: "max-content",
      minWidth: "100%",
      margin: 0,
      borderRadius: `0 0 ${theme.radii.input} ${theme.radii.input}`,
      zIndex: theme.zIndex.dropdown
    }),
    option: (provided, state) => ({
      ...provided,
      padding: `${theme.spacing[1.5]} ${theme.spacing[1.5]}`,
      color: theme.palette.primary.text,
      backgroundColor: state.isFocused ? theme.palette.grayscale.light5 : "transparent"
    }),
    control: (provided, state) => ({
      ...provided,
      height: "53px",
      flexWrap: "nowrap",
      padding: `${theme.spacing[0.75]} ${theme.spacing[0.5]}`,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      backgroundColor: state.isDisabled ? "rgba(255, 255, 255, 0.5)" : theme.palette.grayscale.white,
      outline: state.isFocused ? `1px solid ${theme.palette.primary.main}` : 0,
      border: 0,
      borderRadius: state.menuIsOpen ? `${theme.radii.input} ${theme.radii.input} 0 0` : theme.radii.input,
      boxShadow: "none",
      transition: "none",
      "&:hover": {
        outline: state.isFocused ? `1px solid ${theme.palette.primary.main}` : "1px solid #B3B3B3"
      }
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? theme.palette.primary.text : theme.palette.secondary.main
    }),
    input: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? theme.palette.primary.text : theme.palette.secondary.main
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme.palette.primary.text
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0px 4px"
    }),
    multiValue: (provided) => ({
      ...provided,
      border: "1px solid #E9E9E9",
      padding: "4px 0px"
    }),
    indicatorSeparator: () => ({
      display: "none"
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: theme.palette.primary.main,
      "&:hover": {
        color: theme.palette.primary.main
      }
    })
  };
}
const FilledSelect = ({
  className,
  name,
  placeholder,
  onChange,
  value,
  options,
  disabled,
  loading,
  canDeselect,
  searchable = false,
  noOptionsMessage
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getFilledSelectStyles(theme), [theme]);
  const { t } = useTranslation();
  options = useMemo(() => {
    if (canDeselect) {
      return [{ label: t("input.select"), value: "" }, ...options];
    }
    return options;
  }, [t, options]);
  return /* @__PURE__ */ React.createElement(
    ReactSelect,
    {
      blurInputOnSelect: true,
      isSearchable: searchable,
      className,
      styles,
      name,
      placeholder: placeholder ?? t("input.select"),
      onChange: (item) => onChange == null ? void 0 : onChange((item == null ? void 0 : item.value) ?? null),
      value: options.find((option) => option.value === value),
      options,
      isDisabled: disabled || loading,
      isLoading: loading,
      noOptionsMessage: () => noOptionsMessage ?? t("input.no_options"),
      menuPortalTarget: dropdownRoot
    }
  );
};
const RoundSelect = ({
  className,
  name,
  placeholder,
  onChange,
  value,
  options,
  disabled,
  loading,
  canDeselect,
  searchable = false,
  noOptionsMessage
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getRoundSelectStyles(theme), [theme]);
  const { t } = useTranslation();
  options = useMemo(() => {
    if (canDeselect) {
      return [{ label: t("input.select"), value: "" }, ...options];
    }
    return options;
  }, [t, options]);
  return /* @__PURE__ */ React.createElement(
    ReactSelect,
    {
      blurInputOnSelect: true,
      isSearchable: searchable,
      className,
      styles,
      name,
      placeholder: placeholder ?? t("input.select"),
      onChange: (item) => onChange == null ? void 0 : onChange((item == null ? void 0 : item.value) ?? null),
      value: options.find((option) => option.value === value),
      options,
      isDisabled: disabled || loading,
      isLoading: loading,
      noOptionsMessage: () => noOptionsMessage ?? t("input.no_options"),
      menuPortalTarget: dropdownRoot
    }
  );
};
const FilledMultiSelect = ({
  className,
  name,
  placeholder,
  onChange,
  values = [],
  options,
  disabled,
  loading,
  searchable = false,
  noOptionsMessage
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getFilledSelectStyles(theme), [theme]);
  const { t } = useTranslation();
  return /* @__PURE__ */ React.createElement(
    ReactSelect,
    {
      blurInputOnSelect: true,
      isMulti: true,
      isSearchable: searchable,
      className,
      styles,
      name,
      placeholder: placeholder ?? t("input.select"),
      onChange: (items) => onChange == null ? void 0 : onChange(items.map((item) => item.value)),
      value: options.filter((option) => values.includes(option.value)),
      options,
      isDisabled: disabled || loading,
      isLoading: loading,
      noOptionsMessage: () => noOptionsMessage ?? t("input.no_options"),
      menuPortalTarget: dropdownRoot
    }
  );
};
const RoundMultiSelect = ({
  className,
  name,
  placeholder,
  onChange,
  values = [],
  options,
  disabled,
  loading,
  searchable = false,
  noOptionsMessage
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getRoundSelectStyles(theme), [theme]);
  const { t } = useTranslation();
  return /* @__PURE__ */ React.createElement(
    ReactSelect,
    {
      blurInputOnSelect: true,
      isMulti: true,
      isSearchable: searchable,
      className,
      styles,
      name,
      placeholder: placeholder ?? t("input.select"),
      onChange: (items) => onChange == null ? void 0 : onChange(items.map((item) => item.value)),
      value: options.filter((option) => values.includes(option.value)),
      options,
      isDisabled: disabled || loading,
      isLoading: loading,
      noOptionsMessage: () => noOptionsMessage ?? t("input.no_options"),
      menuPortalTarget: dropdownRoot
    }
  );
};
const RoundTextAreaBase = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "auto"};
  color: ${({ theme }) => theme.palette.primary.text};
  background-color: ${({ theme, disabled }) => disabled ? "rgba(255, 255, 255, 0.5)" : theme.palette.grayscale.white};
  border-radius: ${Styling.radii("input")};
  border: ${({ theme, focus, valid }) => {
  if (focus) {
    return "none";
  }
  if (valid === true) {
    return `2px solid ${theme.palette.success.main} !important`;
  } else if (valid === false) {
    return `2px solid ${theme.palette.error.main} !important`;
  } else {
    return "none";
  }
}};
  outline: ${({ theme, focus }) => focus ? `1px solid ${theme.palette.primary.main}` : 0};

  &:hover {
    outline: ${({ theme, focus }) => focus ? void 0 : `1px solid ${theme.palette.grayscale.light2}`};
  }
`;
const UnderlinedTextAreaBase = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "auto"};
  color: ${({ theme }) => theme.palette.primary.text};
  background-color: ${({ theme, disabled }) => disabled ? theme.palette.grayscale.light5 : theme.palette.grayscale.white};
  border-top-left-radius: ${Styling.radii("input")};
  border-top-right-radius: ${Styling.radii("input")};
  border-bottom: ${({ theme, valid }) => {
  if (valid === true) {
    return `2px solid ${theme.palette.success.main} !important`;
  } else if (valid === false) {
    return `2px solid ${theme.palette.error.main} !important`;
  } else {
    return `2px solid ${theme.palette.grayscale.light5}`;
  }
}};

  &:hover {
    border-bottom: ${({ theme, disabled }) => disabled ? void 0 : `2px solid ${theme.palette.grayscale.light4}`};
  }
`;
const FilledTextAreaBase = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "auto"};
  color: ${({ theme }) => theme.palette.primary.text};
  background-color: #f9fafb; // TODO move to theme
  border-radius: ${Styling.radii("input")};
  border: 1px solid #e9e9e9; // TODO move to theme
  outline: ${({ theme, focus }) => focus ? `1px solid ${theme.palette.primary.main}` : 0};
  transition:
    100ms background-color,
    100ms border;

  &:hover {
    background-color: ${({ theme, disabled }) => disabled ? "#F9FAFB" : theme.palette.grayscale.light5};
    outline: ${({ theme, focus }) => focus ? void 0 : `1px solid ${theme.palette.grayscale.light2}`};
  }
`;
const TextAreaInput = styled.textarea`
  ${Styling.typography("input")}
  padding: ${Styling.spacing(2)};
  padding-left: ${Styling.spacing(2)};
  width: 100% !important;
  border: 0;
  outline: 0;
  background-color: transparent;
  color: inherit;

  &:disabled {
    cursor: not-allowed;
  }
`;
const RoundTextArea = ({
  className,
  valid,
  ...props
}) => {
  const [hasFocus, setHasFocus] = useState(false);
  return /* @__PURE__ */ React.createElement(
    RoundTextAreaBase,
    {
      disabled: props.disabled,
      valid,
      focus: hasFocus,
      className
    },
    /* @__PURE__ */ React.createElement(
      TextAreaInput,
      {
        ...props,
        onFocus: (evt) => {
          var _a;
          setHasFocus(true);
          (_a = props.onFocus) == null ? void 0 : _a.call(props, evt);
        },
        onBlur: (evt) => {
          var _a;
          setHasFocus(false);
          (_a = props.onBlur) == null ? void 0 : _a.call(props, evt);
        }
      }
    )
  );
};
const Underline = styled.div`
  width: ${({ visible }) => visible ? "100%" : "0%"};
  height: 2px;
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${({ theme }) => theme.palette.primary.main};
  transition: width 120ms;
`;
const UnderlinedTextArea = ({
  className,
  valid,
  ...props
}) => {
  const [hasFocus, setHasFocus] = useState(false);
  return /* @__PURE__ */ React.createElement(
    UnderlinedTextAreaBase,
    {
      disabled: props.disabled,
      valid,
      focus: hasFocus,
      className
    },
    /* @__PURE__ */ React.createElement(
      TextAreaInput,
      {
        ...props,
        onFocus: (evt) => {
          var _a;
          setHasFocus(true);
          (_a = props.onFocus) == null ? void 0 : _a.call(props, evt);
        },
        onBlur: (evt) => {
          var _a;
          setHasFocus(false);
          (_a = props.onBlur) == null ? void 0 : _a.call(props, evt);
        }
      }
    ),
    /* @__PURE__ */ React.createElement(Underline, { visible: hasFocus })
  );
};
const FilledTextArea = ({
  className,
  valid,
  ...props
}) => {
  const [hasFocus, setHasFocus] = useState(false);
  return /* @__PURE__ */ React.createElement(
    FilledTextAreaBase,
    {
      disabled: props.disabled,
      valid,
      focus: hasFocus,
      className
    },
    /* @__PURE__ */ React.createElement(
      TextAreaInput,
      {
        ...props,
        onFocus: (evt) => {
          var _a;
          setHasFocus(true);
          (_a = props.onFocus) == null ? void 0 : _a.call(props, evt);
        },
        onBlur: (evt) => {
          var _a;
          setHasFocus(false);
          (_a = props.onBlur) == null ? void 0 : _a.call(props, evt);
        }
      }
    )
  );
};
const Container$a = styled.div`
  position: fixed;
  width: 100%;
  height: 100vh;
  z-index: ${Styling.zIndex("backdrop")};
`;
const Backdrop = ({
  color = "rgba(0, 0, 0, 0.5)",
  durationMs = 100,
  children,
  show,
  onFullyGone,
  ...props
}) => {
  const [isClosed, setIsClosed] = useState(!show);
  useEffect(() => {
    if (show) {
      setIsClosed(false);
    }
  }, [show]);
  const handleGone = () => {
    setIsClosed(true);
    onFullyGone == null ? void 0 : onFullyGone();
  };
  if (isClosed) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Container$a, null, /* @__PURE__ */ React.createElement(
    Overlay$1,
    {
      show,
      color,
      durationMs,
      onFullyGone: handleGone,
      ...props
    }
  ), children);
};
const Menu$2 = styled.nav(
  ({ open, anchor, theme }) => ({
    position: "fixed",
    zIndex: theme.zIndex.drawer,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    height: "100%",
    top: 0,
    left: anchor === "left" ? 0 : void 0,
    right: anchor === "right" ? 0 : void 0,
    backgroundColor: theme.palette.grayscale.light5,
    color: theme.palette.primary.text,
    boxShadow: open ? "rgba(0, 0, 0, 0.2) 0px 8px 10px -5px, rgba(0, 0, 0, 0.14) 0px 16px 24px 2px, rgba(0, 0, 0, 0.12) 0px 6px 30px 5px" : "none",
    transform: (() => {
      if (open) {
        return "translateX(0)";
      }
      return anchor === "left" ? "translateX(-100%)" : "translateX(100%)";
    })(),
    transition: "transform 200ms, box-shadow 200ms"
  })
);
const drawerRoot = document.getElementById("drawer-root");
const Drawer = ({
  anchor = "left",
  open,
  onClose,
  className,
  children
}) => {
  return createPortal(
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Backdrop, { show: open, onClick: onClose }), /* @__PURE__ */ React.createElement(Menu$2, { className, open, anchor }, children)),
    drawerRoot
  );
};
var faBars = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "bars";
  var width = 448;
  var height = 512;
  var aliases = ["navicon"];
  var unicode = "f0c9";
  var svgPathData = "M0 96C0 78.3 14.3 64 32 64H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H32C14.3 128 0 113.7 0 96zM0 256c0-17.7 14.3-32 32-32H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H32c-17.7 0-32-14.3-32-32zM448 416c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32s14.3-32 32-32H416c17.7 0 32 14.3 32 32z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faBars = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faBars);
const Button$3 = styled.button.attrs({
  type: "button",
  "aria-label": "Open navigation menu"
  // TODO translate
})`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  background-color: transparent;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale.light4};
  }
`;
const HamburgerButton = ({ onClick }) => {
  return /* @__PURE__ */ React.createElement(Button$3, { onClick }, /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faBars.faBars, size: "lg" }));
};
const Typography = styled.div`
  ${(props) => Styling.typography(props.type)(props)};
`;
const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 100%;
  // Necessary because parent has pointer-events: none;
  pointer-events: all;
`;
const peekInFromTop = keyframes({
  from: {
    transform: "translateY(-100%)"
  },
  to: {
    transform: "translateY(0%)"
  }
});
const ActiveLight = styled.div(
  ({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    position: "absolute",
    width: "45px",
    height: "3px"
  }),
  css`
    animation: ${peekInFromTop} 100ms linear 1;
  `
);
const List$2 = styled.ul`
  display: flex;
  height: 100%;
  ${({ theme }) => flexGap(theme.spacing[2])};
`;
const ListItem$2 = styled.li({
  display: "flex",
  flexDirection: "column",
  alignItems: "center"
});
const Link$1 = styled(NavLink)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: ${Styling.spacing(0, 3)};
  color: #4d4d4d; // TODO move to theme
  transition:
    color 50ms,
    padding-bottom 50ms;
  white-space: nowrap;
  text-decoration: none;

  &:hover,
  &.active {
    color: ${({ theme }) => theme.palette.primary.main};
    padding-bottom: ${Styling.spacing(1)};
  }
`;
const HeaderNavigation = ({
  label = "navigation",
  paths
}) => {
  const location = useLocation();
  return /* @__PURE__ */ React.createElement(Nav, { "aria-label": label }, /* @__PURE__ */ React.createElement(List$2, null, paths.map((path) => {
    const match = matchPath(location.pathname, {
      path: path.href,
      exact: false,
      strict: false
    }) !== null;
    return /* @__PURE__ */ React.createElement(ListItem$2, { key: path.title }, match && /* @__PURE__ */ React.createElement(ActiveLight, null), /* @__PURE__ */ React.createElement(Link$1, { to: path.href }, /* @__PURE__ */ React.createElement(Typography, { type: "h4", as: "span" }, path.title)));
  })));
};
const BaseImage = styled.img`
  height: 100%;
  width: auto;
  vertical-align: middle;
  object-fit: contain;
`;
const ResponsiveImage = ({
  className,
  ...props
}) => {
  const [loaded, setLoaded] = useState(false);
  return /* @__PURE__ */ React.createElement(
    BaseImage,
    {
      loading: "lazy",
      ...props,
      onLoad: () => setLoaded(true),
      className: `${className} ${loaded ? "loaded" : ""}`
    }
  );
};
const Container$9 = styled(Drawer)`
  width: 100%;
  max-width: 420px;
  @media (max-width: ${Styling.breakpoint("sm")}px) {
    max-width: initial;
  }
`;
const List$1 = styled.ul``;
const ListItem$1 = styled.li``;
const Link = styled(Link$2)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #4d4d4d; // TODO move to theme
  height: 100%;
  padding: ${Styling.spacing(3, 5)};
  transition: color 50ms;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale.light4};
  }

  &:hover,
  &.active {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;
const Button$2 = styled.button.attrs({
  type: "button",
  "aria-label": "Close navigation menu"
  // TODO translate
})`
  display: flex;
  position: fixed;
  right: 0;
  margin: 12px;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  background-color: transparent;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale.light4};
  }
`;
const Logo = styled(ResponsiveImage).attrs({
  alt: "Logo"
  // TODO translate
})`
  max-width: 160px;
  max-height: 120px;
  padding: ${Styling.spacing(3, 0, 2, 5)};
`;
const CloseButton$1 = ({ onClick }) => {
  return /* @__PURE__ */ React.createElement(Button$2, { onClick }, /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faXmark.faXmark, size: "lg" }));
};
const MobileNavDrawer = ({
  logo,
  open,
  onClose,
  items
}) => /* @__PURE__ */ React.createElement(Container$9, { open, onClose }, /* @__PURE__ */ React.createElement(CloseButton$1, { onClick: onClose }), logo && /* @__PURE__ */ React.createElement(Logo, { src: logo }), /* @__PURE__ */ React.createElement(List$1, null, items.map((item) => /* @__PURE__ */ React.createElement(ListItem$1, { key: item.title }, /* @__PURE__ */ React.createElement(Link, { to: item.href, onClick: onClose }, item.icon, /* @__PURE__ */ React.createElement(Typography, { type: "h4" }, item.title))))));
var faUser = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "user";
  var width = 448;
  var height = 512;
  var aliases = [128100, 62144];
  var unicode = "f007";
  var svgPathData = "M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faUser = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faUser);
const Container$8 = styled.div`
  position: relative;
`;
const ProfileButton = styled.button.attrs({
  "aria-label": "Open profile menu"
  // TODO translate
})`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: ${Styling.spacing(0, 1.5)};
  padding-top: 12px;
  ${({ theme }) => flexGap(theme.spacing[1])};
  background-color: ${({ theme }) => theme.palette.grayscale.light5};
  color: ${({ theme }) => theme.palette.grayscale.main};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.5rem;
  overflow: hidden;

  & > * {
    flex-shrink: 0;
  }
`;
const Menu$1 = styled.ul`
  position: absolute;
  min-width: 100%;
  margin-top: -1px;
  top: 100%;
  right: 0;
  background-color: ${({ theme }) => theme.palette.grayscale.white};
  box-shadow: 0 8px 16px 0 #7e7f805c;
  border-radius: ${Styling.radii("button", 0, "button", "button")};
  overflow: hidden;
`;
const MenuItem$1 = styled.li`
  color: ${({ theme }) => theme.palette.primary.text};
  padding: ${Styling.spacing(1.5, 2)};
  text-align: right;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale.light5};
  }
`;
const MobileProfileMenu = ({ items }) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const handleClick = useCallback(
    (event) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    },
    [isOpen, containerRef.current]
  );
  useEffect(() => {
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [handleClick]);
  return /* @__PURE__ */ React.createElement(Container$8, { ref: containerRef }, /* @__PURE__ */ React.createElement(ProfileButton, { onClick: () => setIsOpen((open) => !open) }, /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faUser.faUser, size: "lg" })), isOpen && /* @__PURE__ */ React.createElement(Menu$1, null, /* @__PURE__ */ React.createElement(Typography, { type: "body" }, items.map((item, index) => {
    if (item === "divider") {
      return /* @__PURE__ */ React.createElement(Fragment, { key: index }, /* @__PURE__ */ React.createElement(Divider, { orientation: "horizontal" }));
    }
    return /* @__PURE__ */ React.createElement(
      MenuItem$1,
      {
        key: index,
        onClick: () => {
          var _a;
          setIsOpen(false);
          (_a = item.onClick) == null ? void 0 : _a.call(item);
        }
      },
      item.content
    );
  }))));
};
var faAngleRight = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "angle-right";
  var width = 320;
  var height = 512;
  var aliases = [8250];
  var unicode = "f105";
  var svgPathData = "M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faAngleRight = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faAngleRight);
var faAngleLeft = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "angle-left";
  var width = 320;
  var height = 512;
  var aliases = [8249];
  var unicode = "f104";
  var svgPathData = "M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faAngleLeft = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faAngleLeft);
const Container$7 = styled.div`
  display: inline-flex;
  ${({ theme }) => flexGap(theme.spacing[0.75])};
`;
const Element = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: ${({ theme }) => theme.palette.secondary.main};
`;
const Button$1 = styled(Element).attrs({
  as: "button"
})`
  background-color: ${({ theme }) => theme.palette.grayscale.light4};
  border: 0;
  border-radius: ${Styling.radii("button")};
  cursor: pointer;

  &:hover,
  &.active {
    color: ${({ theme }) => theme.palette.grayscale.white};
    background-color: ${({ theme }) => theme.palette.primary.main};
  }

  &.disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.palette.grayscale.light3};
    background-color: ${({ theme }) => theme.palette.grayscale.light4};
  }
`;
function pagination(page, count, siblings, boundary) {
  const range2 = (start, end) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, i) => start + i);
  };
  const startPages = range2(1, Math.min(boundary, count));
  const endPages = range2(Math.max(count - boundary + 1, boundary + 1), count);
  const middleStart = Math.max(
    Math.min(
      // Natural start
      page - siblings,
      // Lower boundary when page is high
      count - boundary - siblings * 2 - 1
    ),
    // Greater than startPages
    boundary + 2
  );
  const middleEnd = Math.min(
    Math.max(
      // Natural end
      page + siblings,
      // Upper boundary when page is low
      boundary + siblings * 2 + 2
    ),
    // Less than endPages
    endPages.length > 0 ? endPages[0] - 2 : count - 1
  );
  const middlePages = range2(middleStart, middleEnd);
  return [
    ...startPages,
    ...middleStart > boundary + 2 ? ["..."] : boundary + 1 < count - boundary ? [boundary + 1] : [],
    ...middlePages,
    ...middleEnd < count - boundary - 1 ? ["..."] : count - boundary > boundary ? [count - boundary] : [],
    ...endPages
  ];
}
const Pagination = ({
  label = "pagination",
  disabled = false,
  page,
  count,
  siblings = 2,
  boundary = 1,
  onChange
}) => {
  const pages = pagination(page, count, siblings, boundary);
  const nextDisabled = disabled || page >= count;
  const prevDisabled = disabled || page <= 1;
  return /* @__PURE__ */ React.createElement(Container$7, { "aria-label": label }, /* @__PURE__ */ React.createElement(
    Button$1,
    {
      "aria-label": "previous",
      tabIndex: 0,
      className: prevDisabled ? "disabled" : "",
      onClick: () => prevDisabled || (onChange == null ? void 0 : onChange(page - 1))
    },
    /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faAngleLeft.faAngleLeft, size: "xs" })
  ), pages.map((elem, index) => {
    if (elem === "...") {
      return /* @__PURE__ */ React.createElement(Element, { key: index }, "...");
    }
    return /* @__PURE__ */ React.createElement(
      Button$1,
      {
        key: index,
        "aria-label": `${elem}`,
        tabIndex: 0,
        className: page === elem ? "active" : "",
        onClick: () => onChange == null ? void 0 : onChange(elem)
      },
      elem
    );
  }), /* @__PURE__ */ React.createElement(
    Button$1,
    {
      "aria-label": "next",
      tabIndex: 0,
      className: nextDisabled ? "disabled" : "",
      onClick: () => nextDisabled || (onChange == null ? void 0 : onChange(page + 1))
    },
    /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faAngleRight.faAngleRight, size: "xs" })
  ));
};
var faAngleUp = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "angle-up";
  var width = 448;
  var height = 512;
  var aliases = [8963];
  var unicode = "f106";
  var svgPathData = "M201.4 137.4c12.5-12.5 32.8-12.5 45.3 0l160 160c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L224 205.3 86.6 342.6c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l160-160z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faAngleUp = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faAngleUp);
var faAngleDown = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "angle-down";
  var width = 448;
  var height = 512;
  var aliases = [8964];
  var unicode = "f107";
  var svgPathData = "M201.4 342.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 274.7 86.6 137.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faAngleDown = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faAngleDown);
const SENSITIVE_CLASS_NAME = "__sensitive";
const Sensitive = ({ children }) => {
  const addSentryIgnoreClass = (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        className: `${child.props.className} ${SENSITIVE_CLASS_NAME}`
      });
    }
    return /* @__PURE__ */ React.createElement("span", { className: SENSITIVE_CLASS_NAME }, child);
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, React.Children.map(children, addSentryIgnoreClass));
};
const Container$6 = styled.div`
  position: relative;
`;
const Button = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 42px;
  padding: 0 ${Styling.spacing(1.5)};
  ${({ theme }) => flexGap(theme.spacing[1])};
  background-color: ${({ theme }) => theme.palette.grayscale.light5};
  color: black;
  border-radius: ${Styling.radii("button")};
  cursor: pointer;
`;
const ProfileIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding-top: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.grayscale.light3};
  color: ${({ theme }) => theme.palette.grayscale.main};
  overflow: hidden;
`;
const Text = styled.div`
  ${Styling.typography("body")};
  white-space: nowrap;
`;
const ExpandCollapse = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 21px;
  width: 21px;
  padding-top: 1px;
  background-color: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.grayscale.white};
  border-radius: 50%;
`;
const Menu = styled.ul`
  position: absolute;
  min-width: 100%;
  margin-top: -1px;
  top: 100%;
  right: 0;
  background-color: ${({ theme }) => theme.palette.grayscale.white};
  box-shadow: 0 8px 16px 0 #7e7f805c;
  border-radius: ${Styling.radii("button", 0, "button", "button")};
  overflow: hidden;
`;
const MenuItem = styled.li`
  color: ${({ theme }) => theme.palette.primary.text};
  padding: ${Styling.spacing(1.5, 2)};
  text-align: right;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale.light5};
  }
`;
const ProfileMenu = ({ username, items }) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const handleClick = useCallback(
    (event) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    },
    [isOpen, containerRef.current]
  );
  useEffect(() => {
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [handleClick]);
  return /* @__PURE__ */ React.createElement(Container$6, { ref: containerRef }, /* @__PURE__ */ React.createElement(Button, { onClick: () => setIsOpen((open) => !open) }, /* @__PURE__ */ React.createElement(ProfileIcon, null, /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faUser.faUser, size: "lg" })), /* @__PURE__ */ React.createElement(Text, null, /* @__PURE__ */ React.createElement(Sensitive, null, username)), /* @__PURE__ */ React.createElement(ExpandCollapse, null, /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: isOpen ? faAngleUp.faAngleUp : faAngleDown.faAngleDown, size: "xs" }))), isOpen && /* @__PURE__ */ React.createElement(Menu, null, /* @__PURE__ */ React.createElement(Typography, { type: "body" }, items.map((item, index) => {
    if (item === "divider") {
      return /* @__PURE__ */ React.createElement(Fragment, { key: index }, /* @__PURE__ */ React.createElement(Divider, { orientation: "horizontal" }));
    }
    return /* @__PURE__ */ React.createElement(
      MenuItem,
      {
        key: index,
        onClick: () => {
          var _a;
          setIsOpen(false);
          (_a = item.onClick) == null ? void 0 : _a.call(item);
        }
      },
      item.content
    );
  }))));
};
function useWindowSize() {
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  useEffect(() => {
    const updateSize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);
  return size;
}
const Container$5 = styled.div``;
const TabList = styled.div`
  position: relative;
  display: flex;
  ${({ theme }) => flexGap(theme.spacing[2])};
  @media (max-width: ${Styling.breakpoint("md")}px) {
    ${({ theme }) => flexGap(theme.spacing[0])};
  }
`;
const TabItem = styled.button.attrs({
  type: "button"
})`
  display: flex;
  align-items: center;
  ${({ theme }) => flexGap(theme.spacing[1])};
  padding: ${Styling.spacing(1, 2)};
  color: ${({ theme }) => theme.palette.secondary.main};
  cursor: pointer;
  white-space: nowrap;
  box-sizing: content-box;
  background-color: inherit;
  border: 0;

  &:hover,
  &.active {
    color: ${({ theme }) => theme.palette.primary.main};
  }

  &:disabled {
    color: ${({ theme }) => theme.palette.grayscale.light2};
    cursor: not-allowed;
  }
`;
const TabItemText = styled.div`
  ${Styling.typography("body")};
  font-size: 1.05rem;

  &.active {
    font-weight: bold;
  }
`;
const ActiveIndicator = styled.div`
  background-color: ${({ theme }) => theme.palette.primary.main};
  position: absolute;
  bottom: 0;
  left: 0;
  height: 1px;
  width: 0;
  transition:
    left 100ms,
    width 100ms;
`;
const Tabs = ({
  label = "tabs",
  active,
  tabs,
  onChange
}) => {
  const [activeTabElement, setActiveTabElement] = useState(null);
  const activeIndicatorRef = useRef(null);
  const windowSize = useWindowSize();
  useEffect(() => {
    if (activeTabElement && activeIndicatorRef.current) {
      activeIndicatorRef.current.style.width = `${activeTabElement.offsetWidth}px`;
      activeIndicatorRef.current.style.left = `${activeTabElement.offsetLeft}px`;
    }
  }, [activeTabElement, windowSize]);
  const handleClick = (tab) => onChange == null ? void 0 : onChange(tab.name);
  return /* @__PURE__ */ React.createElement(Container$5, null, /* @__PURE__ */ React.createElement(TabList, { role: "tablist", "aria-label": label }, tabs.map((tab) => /* @__PURE__ */ React.createElement(
    TabItem,
    {
      role: "tab",
      tabIndex: 0,
      "aria-label": tab.name,
      "aria-selected": tab.name === active,
      key: tab.name,
      disabled: tab.disabled,
      ref: tab.name === active ? (el) => setActiveTabElement(el) : void 0,
      className: tab.name === active ? "active" : "",
      onClick: () => handleClick(tab)
    },
    tab.icon,
    /* @__PURE__ */ React.createElement(TabItemText, { className: tab.name === active ? "active" : "" }, tab.name)
  )), /* @__PURE__ */ React.createElement(ActiveIndicator, { ref: activeIndicatorRef })));
};
const Bold = styled.span`
  font-weight: bold;
`;
const List = styled.ol`
  display: flex;
  flex-direction: column;
  ${({ theme }) => flexGap(theme.spacing[2])};
  width: 100%;
`;
const ListItem = styled.li`
  display: flex;
  flex-direction: row;
  ${({ theme }) => flexGap(theme.spacing[1.5])};
`;
const Bullet = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.palette.grayscale.white};
  background-color: ${({ theme }) => theme.palette.primary.main};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  padding-bottom: 2px;
  font-size: 0.8rem;
`;
const OrderedList = ({ items }) => {
  return /* @__PURE__ */ React.createElement(List, null, items.map((item, index) => /* @__PURE__ */ React.createElement(ListItem, { key: index }, /* @__PURE__ */ React.createElement(Bullet, null, index + 1), /* @__PURE__ */ React.createElement(Typography, { type: "body" }, item))));
};
const Checkmark = ({ value }) => {
  const theme = useTheme();
  return value ? /* @__PURE__ */ React.createElement(
    FontAwesomeIcon,
    {
      icon: faCheck.faCheck,
      color: theme.palette.success.light1,
      size: "lg"
    }
  ) : /* @__PURE__ */ React.createElement(
    FontAwesomeIcon,
    {
      icon: faXmark.faXmark,
      color: theme.palette.error.light1,
      size: "lg"
    }
  );
};
const Code = styled.div`
  ${Styling.typography("body")}
  padding: 16px;
  color: #4d4d4d;
  background-color: #f9fafb;
  border-radius: 4px;
  border: 1px solid #e9e9e9;
  overflow: auto;
`;
var faAnglesUp = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "angles-up";
  var width = 448;
  var height = 512;
  var aliases = ["angle-double-up"];
  var unicode = "f102";
  var svgPathData = "M246.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L224 109.3 361.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160zm160 352l-160-160c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L224 301.3 361.4 438.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faAnglesUp = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faAnglesUp);
var faAnglesDown = {};
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  var prefix = "fas";
  var iconName = "angles-down";
  var width = 448;
  var height = 512;
  var aliases = ["angle-double-down"];
  var unicode = "f103";
  var svgPathData = "M246.6 470.6c-12.5 12.5-32.8 12.5-45.3 0l-160-160c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L224 402.7 361.4 265.4c12.5-12.5 32.8-12.5 45.3 0s12.5 32.8 0 45.3l-160 160zm160-352l-160 160c-12.5 12.5-32.8 12.5-45.3 0l-160-160c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L224 210.7 361.4 73.4c12.5-12.5 32.8-12.5 45.3 0s12.5 32.8 0 45.3z";
  exports.definition = {
    prefix,
    iconName,
    icon: [
      width,
      height,
      aliases,
      unicode,
      svgPathData
    ]
  };
  exports.faAnglesDown = exports.definition;
  exports.prefix = prefix;
  exports.iconName = iconName;
  exports.width = width;
  exports.height = height;
  exports.ligatures = aliases;
  exports.unicode = unicode;
  exports.svgPathData = svgPathData;
  exports.aliases = aliases;
})(faAnglesDown);
const Container$4 = styled.div`
  position: relative;
  overflow-y: hidden;
  word-break: break-word;
`;
const Content = styled.div``;
const Overlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  background: linear-gradient(180deg, transparent 40%, white calc(100% - 20px));
`;
const Bar = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 100%;
  height: 20px;
  bottom: 0;
  border: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.grayscale.white};
  background: ${({ theme }) => theme.palette.grayscale.light3};

  &:hover {
    background: ${({ theme }) => theme.palette.grayscale.light2};
  }

  &:active {
    background: ${({ theme }) => theme.palette.grayscale.light3};
  }
`;
const Expandable = ({
  className,
  style,
  maxHeight,
  children
}) => {
  const [open, setOpen] = useState(false);
  const [containerElement, setContainerElement] = useState(null);
  const [contentElement, setContentElement] = useState(
    null
  );
  const [overflows, setOverflows] = useState(false);
  const windowSize = useWindowSize();
  useEffect(() => {
    if (containerElement && contentElement) {
      setOverflows(containerElement.clientHeight < contentElement.clientHeight);
    }
  }, [windowSize, containerElement, contentElement]);
  useEffect(() => {
    if (!overflows) {
      setOpen(false);
    }
  }, [overflows]);
  const toggleOpen = () => setOpen((open2) => !open2);
  return /* @__PURE__ */ React.createElement(
    Container$4,
    {
      ref: (ref) => setContainerElement(ref),
      className,
      style: {
        ...style,
        paddingBottom: overflows ? "24px" : void 0,
        maxHeight: open ? void 0 : maxHeight
      }
    },
    /* @__PURE__ */ React.createElement(Content, { ref: (ref) => setContentElement(ref) }, children),
    overflows && /* @__PURE__ */ React.createElement(React.Fragment, null, !open && /* @__PURE__ */ React.createElement(Overlay, null), /* @__PURE__ */ React.createElement(Bar, { onClick: toggleOpen, title: "" }, /* @__PURE__ */ React.createElement(
      FontAwesomeIcon,
      {
        icon: open ? faAnglesUp.faAnglesUp : faAnglesDown.faAnglesDown,
        size: "sm"
      }
    )))
  );
};
const POPIN_DURATION = 20;
const FADEOUT_DURATION = 150;
const popIn = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0%);
  }
`;
const Container$3 = styled(Box).attrs(({ theme }) => ({
  spacing: theme.spacing[2],
  alignX: "space-between",
  alignY: "center"
}))`
  align-items: center;
  color: ${({ theme }) => theme.palette.primary.text};
  background-color: ${({ theme }) => theme.palette.grayscale.white};
  box-shadow: ${Styling.shadow("notification")};
  padding: ${Styling.spacing(2, 2, 2, 3)};
  border-radius: ${Styling.radii("notification")};
  z-index: ${Styling.zIndex("notification")};
  opacity: ${({ show }) => show ? 1 : 0};
  animation: ${popIn} ${POPIN_DURATION}ms linear 1;
  transition: opacity ${FADEOUT_DURATION}ms;

  &.info {
    border-left: 8px solid #3f78e3;
  }

  &.success {
    border-left: 8px solid #7be33f;
  }

  &.warn {
    border-left: 8px solid #fdbd3e;
  }

  &.error {
    border-left: 8px solid #fd3f3f;
  }
`;
const CloseButton = styled.button.attrs({
  type: "button"
})`
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  transition:
    color 80ms,
    background-color 80ms;
  height: 26px;
  width: 26px;
  color: #a5a5a5; // TODO move to theme
  background-color: ${({ theme }) => theme.palette.disabled.main};

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
    background-color: ${({ theme }) => theme.palette.grayscale.white};
  }

  &:active {
    background-color: ${({ theme }) => theme.palette.disabled.main};
    color: #a5a5a5; // TODO move to theme
  }
`;
const Notification = ({
  className,
  open,
  type,
  autoHideDurationMs,
  closeable = true,
  onClose,
  children
}) => {
  const [isClosing, setIsClosing] = useState(!open);
  const [isHovering, setIsHovering] = useState(false);
  useEffect(() => setIsClosing(!open), [open]);
  useEffect(() => {
    if (isHovering) {
      return;
    } else if (!autoHideDurationMs && closeable) {
      return;
    }
    const handle = setTimeout(handleClose, autoHideDurationMs ?? 5e3);
    return () => clearTimeout(handle);
  }, [autoHideDurationMs, isHovering]);
  useEffect(() => {
    if (isHovering || !isClosing) {
      return;
    }
    const handle = setTimeout(() => onClose == null ? void 0 : onClose(), FADEOUT_DURATION);
    return () => clearTimeout(handle);
  }, [isClosing, isHovering]);
  const handleClose = () => setIsClosing(true);
  return /* @__PURE__ */ React.createElement(
    Container$3,
    {
      show: !isClosing,
      className: `${className} ${type}`,
      onMouseEnter: () => setIsHovering(true),
      onMouseLeave: () => setIsHovering(false)
    },
    children,
    /* @__PURE__ */ React.createElement(CloseButton, { onClick: handleClose }, /* @__PURE__ */ React.createElement(FontAwesomeIcon, { icon: faXmark.faXmark, size: "lg" }))
  );
};
const signatureGreen = "data:image/svg+xml,%3c?xml%20version='1.0'%20encoding='utf-8'?%3e%3c!--%20Generator:%20Adobe%20Illustrator%2019.0.0,%20SVG%20Export%20Plug-In%20.%20SVG%20Version:%206.00%20Build%200)%20--%3e%3csvg%20version='1.1'%20id='Layer_1'%20xmlns='http://www.w3.org/2000/svg'%20xmlns:xlink='http://www.w3.org/1999/xlink'%20x='0px'%20y='0px'%20viewBox='0%200%2096%2096'%20style='enable-background:new%200%200%2096%2096;'%20xml:space='preserve'%3e%3cstyle%20type='text/css'%3e%20.st0{fill:%23178610;}%20%3c/style%3e%3cg%20id='XMLID_1_'%3e%3cpath%20id='XMLID_5_'%20class='st0'%20d='M79.2,44.2l-4.4-5.4c-0.7-0.9-1.1-1.9-1.3-3.1l-0.8-6.9c-0.3-2.8-2.6-5.1-5.4-5.4l-6.9-0.8%20c-1.2-0.1-2.3-0.6-3.2-1.3l-5.4-4.4c-2.2-1.8-5.4-1.8-7.6,0l-5.4,4.4c-0.9,0.7-1.9,1.1-3.1,1.3l-6.9,0.8c-2.8,0.3-5.1,2.6-5.4,5.4%20l-0.8,6.9c-0.1,1.2-0.6,2.3-1.3,3.2l-4.4,5.4c-1.8,2.2-1.8,5.4,0,7.6l4.4,5.4c0.7,0.9,1.1,1.9,1.3,3.1l0.8,6.9%20c0.3,2.8,2.6,5.1,5.4,5.4l6.9,0.8c1.2,0.1,2.3,0.6,3.2,1.3l5.4,4.4c2.2,1.8,5.4,1.8,7.6,0l5.4-4.4c0.9-0.7,1.9-1.1,3.1-1.3l6.9-0.8%20c2.8-0.3,5.1-2.6,5.4-5.4l0.8-6.9c0.1-1.2,0.6-2.3,1.3-3.2l4.4-5.4C80.9,49.6,80.9,46.4,79.2,44.2z%20M41.9,64.3L27.7,50.1l6.1-6.1%20l8.1,8.1l20.3-20.3l6.1,6.3L41.9,64.3z'/%3e%3c/g%3e%3c/svg%3e";
const signatureGray = "data:image/svg+xml,%3c?xml%20version='1.0'%20encoding='utf-8'?%3e%3c!--%20Generator:%20Adobe%20Illustrator%2019.0.0,%20SVG%20Export%20Plug-In%20.%20SVG%20Version:%206.00%20Build%200)%20--%3e%3csvg%20version='1.1'%20id='Layer_1'%20xmlns='http://www.w3.org/2000/svg'%20xmlns:xlink='http://www.w3.org/1999/xlink'%20x='0px'%20y='0px'%20viewBox='0%200%2096%2096'%20style='enable-background:new%200%200%2096%2096;'%20xml:space='preserve'%3e%3cstyle%20type='text/css'%3e%20.st0{fill:%23CCCCCC;}%20%3c/style%3e%3cg%20id='XMLID_1_'%3e%3cpath%20id='signaturePath'%20class='st0'%20d='M79.2,44.2l-4.4-5.4c-0.7-0.9-1.1-1.9-1.3-3.1l-0.8-6.9c-0.3-2.8-2.6-5.1-5.4-5.4l-6.9-0.8%20c-1.2-0.1-2.3-0.6-3.2-1.3l-5.4-4.4c-2.2-1.8-5.4-1.8-7.6,0l-5.4,4.4c-0.9,0.7-1.9,1.1-3.1,1.3l-6.9,0.8c-2.8,0.3-5.1,2.6-5.4,5.4%20l-0.8,6.9c-0.1,1.2-0.6,2.3-1.3,3.2l-4.4,5.4c-1.8,2.2-1.8,5.4,0,7.6l4.4,5.4c0.7,0.9,1.1,1.9,1.3,3.1l0.8,6.9%20c0.3,2.8,2.6,5.1,5.4,5.4l6.9,0.8c1.2,0.1,2.3,0.6,3.2,1.3l5.4,4.4c2.2,1.8,5.4,1.8,7.6,0l5.4-4.4c0.9-0.7,1.9-1.1,3.1-1.3l6.9-0.8%20c2.8-0.3,5.1-2.6,5.4-5.4l0.8-6.9c0.1-1.2,0.6-2.3,1.3-3.2l4.4-5.4C80.9,49.6,80.9,46.4,79.2,44.2z%20M41.9,64.3L27.7,50.1l6.1-6.1%20l8.1,8.1l20.3-20.3l6.1,6.3L41.9,64.3z'/%3e%3c/g%3e%3c/svg%3e";
const SignatureIcon = styled.img.attrs(
  ({ signed }) => ({
    src: signed ? signatureGreen : signatureGray,
    alt: ""
  })
)`
  height: 36px;
  width: 36px;
`;
const tooltipRoot = document.getElementById("tooltip-root");
const Container$2 = styled.div`
  position: absolute;
  z-index: ${Styling.zIndex("tooltip")};
  box-shadow: ${Styling.shadow("tooltip")};
`;
var TooltipAlignment = /* @__PURE__ */ ((TooltipAlignment2) => {
  TooltipAlignment2[TooltipAlignment2["LEFT"] = 0] = "LEFT";
  TooltipAlignment2[TooltipAlignment2["TOP"] = 1] = "TOP";
  TooltipAlignment2[TooltipAlignment2["RIGHT"] = 2] = "RIGHT";
  TooltipAlignment2[TooltipAlignment2["BOTTOM"] = 3] = "BOTTOM";
  return TooltipAlignment2;
})(TooltipAlignment || {});
const Tooltip = ({
  className,
  content,
  align = 1,
  spacing = 8,
  children
}) => {
  const windowSize = useWindowSize();
  const [show, setShow] = useState(false);
  const [style, setStyle] = useState(void 0);
  const [containerRef, setContainerRef] = useState();
  const [tooltipRef, setTooltipRef] = useState();
  const updateCoords = useCallback(() => {
    if (!containerRef || !tooltipRef) {
      return;
    }
    const containerRect = containerRef.getBoundingClientRect();
    const tooltipRect = tooltipRef.getBoundingClientRect();
    const minX = window.scrollX + spacing;
    const maxX = window.innerWidth + window.scrollX - spacing;
    const minY = window.scrollY + spacing;
    const maxY = window.innerHeight + window.scrollY - spacing;
    switch (align) {
      case 0:
        setStyle({
          left: clamp(
            containerRect.x - spacing + window.scrollX,
            minX + tooltipRect.width,
            maxX
          ),
          top: clamp(
            containerRect.y + containerRect.height / 2 + window.scrollY,
            minY + tooltipRect.height / 2,
            maxY - tooltipRect.height / 2
          ),
          transform: "translate(-100%, -50%)"
        });
        break;
      case 1:
        setStyle({
          left: clamp(
            containerRect.x + containerRect.width / 2 + window.scrollX,
            minX + tooltipRect.width / 2,
            maxX - tooltipRect.width / 2
          ),
          top: clamp(
            containerRect.y - spacing + window.scrollY,
            minY + tooltipRect.height,
            maxY
          ),
          transform: "translate(-50%, -100%)"
        });
        break;
      case 2:
        setStyle({
          left: clamp(
            containerRect.x + containerRect.width + spacing + window.scrollX,
            minX,
            maxX - tooltipRect.width
          ),
          top: clamp(
            containerRect.y + containerRect.height / 2 + window.scrollY,
            minY + tooltipRect.height / 2,
            maxY - tooltipRect.height / 2
          ),
          transform: "translate(0%, -50%)"
        });
        break;
      case 3:
        setStyle({
          left: clamp(
            containerRect.x + containerRect.width / 2 + window.scrollX,
            minX + tooltipRect.width / 2,
            maxX - tooltipRect.width / 2
          ),
          top: clamp(
            containerRect.y + containerRect.height + spacing + window.scrollY,
            minY,
            maxY - tooltipRect.height
          ),
          transform: "translate(-50%, 0%)"
        });
        break;
    }
  }, [windowSize, containerRef, tooltipRef, align, spacing]);
  useEffect(() => updateCoords(), [updateCoords, show]);
  useEffect(() => {
    if (containerRef) {
      const show2 = () => setShow(true);
      const hide = () => setShow(false);
      containerRef.addEventListener("mouseenter", show2);
      containerRef.addEventListener("mouseleave", hide);
      return () => {
        containerRef.removeEventListener("mouseenter", show2);
        containerRef.removeEventListener("mouseleave", hide);
      };
    }
  }, [containerRef]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", { ref: (el) => setContainerRef(el) }, children), show && createPortal(
    /* @__PURE__ */ React.createElement(
      Container$2,
      {
        ref: (el) => setTooltipRef(el),
        className,
        style
      },
      content
    ),
    tooltipRoot
  ));
};
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    Sentry.reportError("Error boundary hit", { error: { error, info } });
  }
  render() {
    if (this.state.error !== null) {
      const Fallback = this.props.fallback;
      return /* @__PURE__ */ React.createElement(Fallback, { error: this.state.error });
    }
    return this.props.children;
  }
}
function useIsMounted() {
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return useCallback(() => mounted.current, []);
}
var AsyncState = /* @__PURE__ */ ((AsyncState2) => {
  AsyncState2[AsyncState2["SUCCESS"] = 0] = "SUCCESS";
  AsyncState2[AsyncState2["ERROR"] = 1] = "ERROR";
  AsyncState2[AsyncState2["PENDING"] = 2] = "PENDING";
  return AsyncState2;
})(AsyncState || {});
function useAsync(func, deps) {
  const async = useAsyncLazy(func, deps);
  useEffect(() => {
    void async.reload();
  }, []);
  return async;
}
function useAsyncLazy(func, deps) {
  const isMounted = useIsMounted();
  const isFirstRender = useRef(true);
  const [data, setData] = useState({
    state: 2,
    value: null,
    error: null
  });
  const reload = useCallback(async (clean = false, signal) => {
    if (clean) {
      setData({
        state: 2,
        value: null,
        error: null
      });
    } else {
      setData((data2) => ({
        ...data2,
        state: 2
        /* PENDING */
      }));
    }
    try {
      const result = await func(signal);
      if (isMounted() && !(signal == null ? void 0 : signal.aborted)) {
        setData({
          state: 0,
          value: result,
          error: null
        });
      }
    } catch (e) {
      if (isMounted() && !(signal == null ? void 0 : signal.aborted)) {
        setData({
          state: 1,
          value: null,
          error: e
        });
      }
    }
  }, deps);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const controller = new AbortController();
    void reload(false, controller.signal);
    return () => controller.abort();
  }, [reload]);
  return useMemo(() => ({ ...data, reload }), [data, reload]);
}
function useBreakPoint(name) {
  const theme = useTheme();
  const size = useWindowSize();
  const above = useMemo(
    () => size.width > theme.breakpoints[name],
    [size, theme, name]
  );
  const below = useMemo(
    () => size.width <= theme.breakpoints[name],
    [size, theme, name]
  );
  return useMemo(() => ({ above, below }), [above, below]);
}
const DialogsContext = createContext(null);
const dialogRoot = document.getElementById("dialog-root");
const popUp = keyframes`
  from {
    transform: translate(-50%, calc(-50% + 10px));
  }
  to {
    transform: translate(-50%, -50%);
  }
`;
const FADE_DURATION_MS = 100;
const Container$1 = styled(Box).attrs({
  direction: "horizontal",
  alignX: "center"
})`
  position: absolute;
  width: 100%;
  top: 50%;
  left: 50%;
  padding: ${Styling.spacing(2)};
  transform: translate(-50%, -50%);
  z-index: ${Styling.zIndex("dialog")};
  opacity: ${({ show }) => show ? 1 : 0};
  animation: ${popUp} ${FADE_DURATION_MS}ms linear 1;
  transition: opacity ${FADE_DURATION_MS}ms;
`;
const DialogProvider = ({ children }) => {
  const [dialogs, setDialogs] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const context = useMemo(
    () => ({
      show(component, options) {
        const dialog2 = { component, options: options ?? {} };
        setDialogs((dialogs2) => [...dialogs2, dialog2]);
        return {
          close() {
            handleClose(dialog2);
          }
        };
      }
    }),
    []
  );
  const dialog = useMemo(() => {
    if (dialogs.length === 0) {
      return null;
    }
    return dialogs[dialogs.length - 1];
  }, [dialogs]);
  useEffect(() => {
    setIsOpen(dialogs.length > 0);
  }, [dialogs]);
  useEffect(() => {
    const handleKeyDown = (evt) => {
      if (evt.key === "Escape" && dialog && dialog.options.closeOnEscape !== false) {
        handleClose(dialog);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dialog]);
  const handleClose = (dialog2) => {
    const doClose = () => {
      setDialogs((dialogs2) => {
        const copy = dialogs2.slice();
        const index = copy.indexOf(dialog2);
        if (index > -1) {
          copy.splice(index, 1);
        }
        return copy;
      });
    };
    if (dialogs.length <= 1) {
      doClose();
    } else {
      setIsOpen(false);
      setTimeout(doClose, FADE_DURATION_MS + 24);
    }
  };
  const handleBackdropClick = () => {
    if (dialog && dialog.options.closeOnBackgroundClick !== false) {
      handleClose(dialog);
    }
  };
  return /* @__PURE__ */ React.createElement(DialogsContext.Provider, { value: context }, children, createPortal(
    /* @__PURE__ */ React.createElement(
      Backdrop,
      {
        show: dialogs.length > 0,
        durationMs: FADE_DURATION_MS,
        onClick: handleBackdropClick
      },
      /* @__PURE__ */ React.createElement(
        Container$1,
        {
          onClick: (e) => {
            if (e.currentTarget === e.target) {
              handleBackdropClick();
            }
          },
          show: isOpen
        },
        dialog && /* @__PURE__ */ React.createElement(dialog.component, { onClose: () => handleClose(dialog) })
      )
    ),
    dialogRoot
  ));
};
function useDialog() {
  const context = useContext(DialogsContext);
  if (!context) {
    throw new Error("useDialog hook must be used within a DialogsProvider");
  }
  return context;
}
function useFocus() {
  const ref = useRef(null);
  useEffect(() => {
    var _a;
    (_a = ref.current) == null ? void 0 : _a.focus();
  }, []);
  return ref;
}
function useForceRerender(deps = []) {
  const [_count, setCount] = useState(0);
  return useCallback(() => setCount((count) => count + 1), deps);
}
var StorageType = /* @__PURE__ */ ((StorageType2) => {
  StorageType2["LOCAL"] = "localStorage";
  StorageType2["SESSION"] = "sessionStorage";
  return StorageType2;
})(StorageType || {});
const NOOP_STORAGE = {
  length: 0,
  clear: () => {
  },
  getItem: () => null,
  key: () => null,
  removeItem: () => {
  },
  setItem: () => {
  }
};
function isStorageSupported(type) {
  const storage = window[type];
  if (!storage) {
    return false;
  }
  try {
    const key = "support-check";
    storage.setItem(key, "");
    storage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
}
function getStorage(type) {
  if (isStorageSupported(type)) {
    return window[type];
  }
  console.warn(`${type} is not supported, using NOOP implementation`);
  return NOOP_STORAGE;
}
const LocalStorage = getStorage(
  "localStorage"
  /* LOCAL */
);
const SessionStorage = getStorage(
  "sessionStorage"
  /* SESSION */
);
function useLocalStorage(key) {
  const [value, setValue] = useState(null);
  const set = useCallback(
    (value2) => LocalStorage.setItem(key, value2),
    [key]
  );
  const clear = useCallback(() => LocalStorage.removeItem(key), [key]);
  const onChange = useCallback(
    (event) => {
      if (event.key === key) {
        setValue(event.newValue);
      }
    },
    [key]
  );
  useEffect(() => {
    setValue(LocalStorage.getItem(key) ?? null);
  }, []);
  useEffect(() => {
    window.addEventListener("storage", onChange);
    return () => window.removeEventListener("storage", onChange);
  }, [onChange]);
  return useMemo(() => ({ value, set, clear }), [value, set, clear]);
}
const NotificationsContext = createContext(null);
const notificationRoot = document.getElementById("notification-root");
let currentId = 0;
const Container = styled(Box).attrs(({ theme }) => ({
  direction: "vertical",
  spacing: theme.spacing[2]
}))(({ theme }) => ({
  position: "fixed",
  left: 0,
  bottom: 0,
  width: "auto",
  padding: theme.spacing[2],
  zIndex: theme.zIndex.notification,
  [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
    width: "100%"
  }
}));
const NotificationProvider = ({
  children
}) => {
  const [notifications, setNotifications] = useState([]);
  const context = useMemo(
    () => ({
      show(content, type, timeout) {
        const key = String(currentId++);
        const notification = { key, content, type, timeout };
        setNotifications((notifications2) => [...notifications2, notification]);
        return {
          close() {
            handleClose(notification);
          }
        };
      }
    }),
    []
  );
  const components = useMemo(
    () => notifications.map((notification) => /* @__PURE__ */ React.createElement(
      Notification,
      {
        open: true,
        key: notification.key,
        onClose: () => handleClose(notification),
        type: notification.type,
        autoHideDurationMs: notification.timeout
      },
      notification.content
    )),
    [notifications]
  );
  const handleClose = (notification) => {
    setNotifications((notifications2) => {
      const copy = notifications2.slice();
      const index = copy.indexOf(notification);
      if (index > -1) {
        copy.splice(index, 1);
      }
      return copy;
    });
  };
  return /* @__PURE__ */ React.createElement(NotificationsContext.Provider, { value: context }, children, components.length > 0 && createPortal(/* @__PURE__ */ React.createElement(Container, null, components), notificationRoot));
};
function useNotification() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotification hook must be used within a NotificationsProvider"
    );
  }
  return context;
}
function useQueryParams() {
  const history = useHistory();
  const location = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    const entries = Object.fromEntries(params);
    return {
      get(key) {
        return entries[key] ?? null;
      },
      set(key, value, silent = true) {
        if (value === null) {
          params.delete(key);
        } else {
          params.set(key, value);
        }
        const descriptor = {
          pathname: location.pathname,
          search: "?" + params
        };
        if (silent) {
          history.replace(descriptor, location.state);
        } else {
          history.push(descriptor, location.state);
        }
      }
    };
  }, [location.search]);
}
const INTERVAL = 2e4;
const EPSILON = 5e3;
function useSuspended(func, deps) {
  useEffect(() => {
    let lastRun = (performance ?? Date).now();
    const id = setInterval(() => {
      const now = (performance ?? Date).now();
      const dt = now - lastRun;
      if (dt > INTERVAL + EPSILON) {
        func(dt);
      }
      lastRun = now;
    }, INTERVAL);
    return () => clearInterval(id);
  }, deps);
}
function useVisibility() {
  const [visibility, setVisibility] = useState(
    document.visibilityState
  );
  useEffect(() => {
    const handleVisibilityChange = () => setVisibility(document.visibilityState);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);
  return visibility;
}
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.dispatchEvent(
    new MouseEvent(`click`, { bubbles: true, cancelable: true, view: window })
  );
  void wait(200).then(() => URL.revokeObjectURL(url));
}
let subdomain;
let domain;
let tld;
const elements = window.location.hostname.split(".");
if (elements.length > 2) {
  subdomain = elements[0] ?? null;
  domain = elements[1] ?? "";
  tld = elements[2] ?? null;
} else if (elements.length === 2 && elements[1] === "localhost") {
  subdomain = elements[0] ?? null;
  domain = "localhost";
  tld = "se";
} else {
  subdomain = null;
  domain = elements[0] ?? "localhost";
  tld = elements[1] ?? null;
}
const hostname = { subdomain, domain, tld };
function loseFocus() {
  const activeElement = document.activeElement;
  activeElement == null ? void 0 : activeElement.blur();
}
const userAgent = navigator.userAgent;
var Platform = /* @__PURE__ */ ((Platform2) => {
  Platform2[Platform2["IOS"] = 0] = "IOS";
  Platform2[Platform2["ANDROID"] = 1] = "ANDROID";
  Platform2[Platform2["WINDOWS"] = 2] = "WINDOWS";
  Platform2[Platform2["MACOS"] = 3] = "MACOS";
  Platform2[Platform2["LINUX"] = 4] = "LINUX";
  Platform2[Platform2["OTHER"] = 5] = "OTHER";
  return Platform2;
})(Platform || {});
let platform;
if (/Android/.test(userAgent)) {
  platform = 1;
} else if (/iPhone|iPad|iPod/.test(userAgent)) {
  platform = 0;
} else if (/Windows NT/.test(userAgent)) {
  platform = 2;
} else if (/Macintosh/.test(userAgent)) {
  platform = 3;
} else if (/Linux/.test(userAgent)) {
  platform = 4;
} else {
  platform = 5;
}
export {
  AcknowledgeDialog,
  AlternativeActionIconButton,
  AlternativeButton,
  AsyncState,
  AuthCard,
  Backdrop,
  BareCheckbox,
  BlinkingBarsLoader,
  Bold,
  Box,
  Button$4 as Button,
  Checkmark,
  CircleIconButton,
  Code,
  ConfirmDialog,
  DangerActionIconButton,
  DangerButton,
  DataTable,
  DialogProvider,
  Divider,
  DownloadButton,
  Drawer,
  ErrorBoundary,
  ErrorDialog,
  Expandable,
  FileDropArea,
  FilledCheckbox,
  FilledDateInput,
  FilledDateTimeInput,
  FilledMultiSelect,
  FilledPasswordField,
  FilledRandomField,
  FilledSelect,
  FilledTextArea,
  FilledTextField,
  GrayButton,
  HamburgerButton,
  HeaderNavigation,
  IconButton,
  LocalStorage,
  MobileNavDrawer,
  MobileProfileMenu,
  Notification,
  NotificationProvider,
  OkButton,
  OrderedList,
  Overlay$1 as Overlay,
  Pagination,
  Platform,
  PrimaryActionIconButton,
  PrimaryButton,
  ProfileMenu,
  RadioButton,
  RadioGroup,
  RegenerateButton,
  ResponsiveImage,
  RoundDateInput,
  RoundDateTimeInput,
  RoundMultiSelect,
  RoundSelect,
  RoundTextArea,
  RoundTextField,
  SENSITIVE_CLASS_NAME,
  SecondaryButton,
  SecureButton,
  Sensitive,
  SessionStorage,
  SignatureIcon,
  SpinningCircleLoader,
  StandardCard,
  StorageType,
  Styling,
  Tabs,
  TertiaryButton,
  Tooltip,
  TooltipAlignment,
  Typography,
  UnderlinedPasswordField,
  UnderlinedRandomField,
  UnderlinedTextArea,
  UnderlinedTextField,
  downloadBlob,
  flexGap,
  hostname,
  isFlexGapSupported,
  isStorageSupported,
  loseFocus,
  pagination,
  platform,
  useAsync,
  useAsyncLazy,
  useBreakPoint,
  useDialog,
  useFocus,
  useForceRerender,
  useIsMounted,
  useLocalStorage,
  useNotification,
  useQueryParams,
  useSuspended,
  useVisibility,
  useWindowSize
};
//# sourceMappingURL=core-ui.js.map
